/**
 * Ruler Directive - Horizontal ruler for visualizing tabstops, margins, and indents
 * 
 * This directive creates a Word-like horizontal ruler that:
 * - Displays a measurement scale in points (72 points = 1 inch)
 * - Shows visual markers for tabstops with alignment indicators (L, R, C, .)
 * - Displays left margin, left indent (first line/hanging), and right margin indicators
 * - Responds to paragraph selection changes to show appropriate tabstops
 * - Supports show/hide toggle with local storage preference persistence
 * 
 * Usage: <lp-ruler></lp-ruler> or <div lp-ruler></div>
 * 
 * The ruler should be placed inside .lease.lease--editor container to match document width.
 * 
 * @requires TabstopService
 * @requires TabRenderer
 * @see plans/lp-001-tabs/tabstop-contract.md
 */
angular.module('LeasePilot').directive('lpRuler', [
  '$rootScope',
  '$timeout',
  '$sce',
  'TabstopService',
  'TabRenderer',
  function($rootScope, $timeout, $sce, TabstopService, TabRenderer) {
    
    // Configuration constants
    const RULER_HEIGHT = 24; // Height of ruler in pixels
    // TICK_INTERVAL should be 1/4 of STEP_SIZE to align with snapping
    // STEP_SIZE defaults to 18, but window.CONSTS.STEP_SIZE is 36, so use dynamic calculation
    function getTickInterval() {
      const stepSize = getStepSize();
      return stepSize / 4; // Quarter of STEP_SIZE (e.g., 36/4 = 9 points, or 18/4 = 4.5 points)
    }
    const POINTS_PER_INCH = 72; // 72 points = 1 inch
    const MAX_TICK = 14; // Maximum tick position (14 inches)
    const MIN_TICK = 6; // Minimum tick position for right indent constraint (6 inches)
    const MAX_POSITION_POINTS = MAX_TICK * POINTS_PER_INCH; // Maximum position in points (1008 points)
    const LOCAL_STORAGE_KEY = 'lp-ruler-visible';
    
    // Tolerance constants
    const TABSTOP_POSITION_TOLERANCE = 2; // Points - tolerance for matching tabstop positions
    const INDENT_DRAG_TOLERANCE = 100; // Pixels - tolerance for ignoring mouse movement outside ruler during indent drag
    
    // Timing constants
    const ANIMATION_DURATION_MS = 300; // Animation duration in milliseconds
    const INIT_DELAY_MS = 200; // Initial ruler update delay in milliseconds
    const RESIZE_DEBOUNCE_MS = 150; // Resize debounce delay in milliseconds
    const DRAG_SAFEAPPLY_DEBOUNCE_MS = 16; // Drag safeApply debounce delay (~60fps)
    
    // Default document measurements (in points)
    const DEFAULT_PAGE_WIDTH = 612; // 8.5 inches
    const DEFAULT_LEFT_MARGIN = 72; // 1 inch
    const DEFAULT_RIGHT_MARGIN = 72; // 1 inch
    
    /**
     * Gets the STEP_SIZE constant (defaults to 18 points = 0.25 inches)
     * @returns {number} - STEP_SIZE in points
     */
    function getStepSize() {
      return window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 18;
    }
    
    /**
     * Snaps a position in points to the nearest quarter STEP_SIZE interval
     * This allows finer control for indent indicators and tabstops
     * @param {number} positionPoints - Position in points
     * @returns {number} - Snapped position in points
     */
    function snapToQuarterStepSize(positionPoints) {
      const stepSize = getStepSize();
      const quarterStepSize = stepSize / 4;
      return Math.round(positionPoints / quarterStepSize) * quarterStepSize;
    }
    
    // Alignment symbols for tabstop markers (text fallback)
    const ALIGNMENT_SYMBOLS = {
      'left': 'L',
      'right': 'R',
      'center': 'C',
      'decimal': '.'
    };
    
    // Inline SVG icons for tabstop markers (using currentColor for CSS control)
    const ALIGNMENT_SVG_ICONS = {
      'left': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="lp-ruler__tabstop-svg-icon"><rect width="256" height="256" fill="none"/><polyline points="168 128 216 80 168 32" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><polyline points="72 224 72 80 216 80" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>',
      'right': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="lp-ruler__tabstop-svg-icon"><rect width="256" height="256" fill="none"/><polyline points="192 224 192 80 48 80" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><polyline points="96 128 48 80 96 32" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><polyline points="192 224 192 80 48 80" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>',
      'center': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="lp-ruler__tabstop-svg-icon"><rect width="256" height="256" fill="none"/><line x1="128" y1="216" x2="128" y2="40" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><polyline points="56 112 128 40 200 112" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>'
    };
    
    /**
     * Creates the ruler template HTML
     * @param {number} rulerWidth - Width of the ruler content area in points
     * @returns {string} - HTML string for the ruler
     */
    function createRulerTemplate(rulerWidth) {
      // Ruler width represents the content area (between margins)
      const contentWidth = rulerWidth || (DEFAULT_PAGE_WIDTH - DEFAULT_LEFT_MARGIN - DEFAULT_RIGHT_MARGIN);
      
      return `
        <div class="lp-ruler" ng-class="{ 'lp-ruler--hidden': !vm.isVisible }">
          <div class="lp-ruler__content" ng-show="vm.isVisible" ng-style="vm.getContentStyle()">
            <div class="lp-ruler__scale" ng-style="vm.getScaleStyle()">
              <!-- Forbidden zones (padding areas) -->
              <div class="lp-ruler__forbidden-zone lp-ruler__forbidden-zone--left"
                   ng-style="{ width: vm.paddingLeft + 'px' }"
                   title="Forbidden zone - indicators cannot be placed here">
              </div>
              <div class="lp-ruler__forbidden-zone lp-ruler__forbidden-zone--right"
                   ng-style="{ width: vm.paddingRight + 'px' }"
                   title="Forbidden zone - indicators cannot be placed here">
              </div>
              
              <!-- Tick marks and labels are rendered here -->
              <div class="lp-ruler__ticks" ng-bind-html="vm.ticksHtml"></div>
              
              <!-- Left margin indicator -->
              <div class="lp-ruler__margin lp-ruler__margin--left" 
                   ng-style="{ left: vm.leftMarginPosition + 'px' }"
                   title="Left margin: {{ vm.leftMarginPoints }}pt">
                <div class="lp-ruler__margin-indicator">◣</div>
              </div>
              
              <!-- Left indent (first line) indicator - draggable -->
              <div class="lp-ruler__indent lp-ruler__indent--first-line"
                   ng-style="{ left: vm.getFirstLineIndentPosition() + 'pt' }"
                   ng-class="{ 'lp-ruler__indent--dragging': vm.isDraggingIndent && vm.dragIndentType === 'first-line' }"
                   ng-mousedown="vm.handleIndentMouseDown($event, 'first-line')"
                   title="First line indent: {{ vm.getFirstLineIndentPoints() }}pt (drag to adjust)">
                <div class="lp-ruler__indent-indicator">
                <img src="/icons/ruler_first_line_indicator.svg" alt="First line indent" />
                </div>
              </div>

              <!-- Left indent (hanging/paragraph) indicator - main piece (only changes margin-left) -->
              <div class="lp-ruler__indent lp-ruler__indent--hanging"
                   ng-style="{ left: (vm.isDraggingIndent && (vm.dragIndentType === 'hanging' || vm.dragIndentType === 'hanging-ticker') ? vm.dragIndentPosition : vm.hangingIndentPosition) + 'pt' }"
                   ng-class="{ 'lp-ruler__indent--dragging': vm.isDraggingIndent && vm.dragIndentType === 'hanging' }"
                   ng-mousedown="vm.handleIndentMouseDown($event, 'hanging')"
                   title="Hanging indent: {{ vm.isDraggingIndent && (vm.dragIndentType === 'hanging' || vm.dragIndentType === 'hanging-ticker') ? vm.dragIndentPoints : vm.hangingIndentPoints }}pt (drag to adjust margin-left only)">
                <div class="lp-ruler__indent-indicator">
                  <img src="/icons/ruler_hanging_indicator.svg" alt="Hanging indent" />
                </div>
              </div>
              
              <!-- Hanging indent ticker - moves both indicators together -->
              <div class="lp-ruler__indent lp-ruler__indent--hanging-ticker"
                   ng-style="{ left: vm.getHangingTickerIndentPosition() + 'pt' }"
                   ng-class="{ 'lp-ruler__indent--dragging': vm.isDraggingIndent && vm.dragIndentType === 'hanging-ticker' }"
                   ng-mousedown="vm.handleIndentMouseDown($event, 'hanging-ticker')"
                   title="Move both indicators together">
                <div class="lp-ruler__indent-indicator lp-ruler__indent-indicator--ticker">
                  <img src="/icons/ruler_hanging_indicator_ticker.svg" alt="Hanging indent ticker" />
                </div>
              </div>
              
              <!-- Right margin indicator -->
              <div class="lp-ruler__margin lp-ruler__margin--right"
                   ng-style="{ right: vm.rightMarginPosition + 'px' }"
                   title="Right margin: {{ vm.rightMarginPoints }}pt">
                <div class="lp-ruler__margin-indicator">◢</div>
              </div>
              
              <!-- Right indent indicator - draggable -->
              <div class="lp-ruler__indent lp-ruler__indent--right-indent"
                   ng-style="{ right: vm.getRightIndentPosition() + 'px' }"
                   ng-class="{ 'lp-ruler__indent--dragging': vm.isDraggingIndent && vm.dragIndentType === 'right-indent' }"
                   ng-mousedown="vm.handleIndentMouseDown($event, 'right-indent')"
                   title="Right indent: {{ vm.getRightIndentPoints() }}pt (drag to adjust)">
                <div class="lp-ruler__indent-indicator">
                  <img src="/icons/ruler_hanging_indicator.svg" alt="Right indent" />
                </div>
              </div>
              
              <!-- Click zone for adding tabstops -->
              <div class="lp-ruler__click-zone" ng-click="vm.handleRulerClick($event)"></div>

              <!-- Multi-paragraph selection indicator -->
              <div class="lp-ruler__multi-select-indicator" ng-show="vm.isMultiParagraphSelection">
                <span class="lp-ruler__multi-select-badge">{{ vm.selectedParagraphCount }} paragraphs selected</span>
              </div>

              <!-- Partial tabstop markers (exist in some but not all selected paragraphs) -->
              <div class="lp-ruler__tabstops lp-ruler__tabstops--partial" ng-show="vm.isMultiParagraphSelection && vm.partialTabstops.length > 0">
                <div class="lp-ruler__tabstop lp-ruler__tabstop--partial"
                     ng-repeat="tabstop in vm.partialTabstops track by $index"
                     ng-style="{ left: vm.getTabstopPosition(tabstop) + 'pt' }"
                     ng-class="{
                       'lp-ruler__tabstop--left': tabstop.alignment === 'left',
                       'lp-ruler__tabstop--right': tabstop.alignment === 'right',
                       'lp-ruler__tabstop--center': tabstop.alignment === 'center',
                       'lp-ruler__tabstop--decimal': tabstop.alignment === 'decimal'
                     }"
                     title="{{ vm.getPartialTabstopTitle(tabstop) }}">
                  <span class="lp-ruler__tabstop-symbol">
                    <span ng-if="vm.shouldUseTabstopIcon(tabstop)" 
                          ng-bind-html="vm.getTabstopIconHtml(tabstop)"></span>
                    <span ng-if="!vm.shouldUseTabstopIcon(tabstop)">{{ vm.getTabstopSymbol(tabstop) }}</span>
                  </span>
                </div>
              </div>

              <!-- Tabstop markers -->
              <div class="lp-ruler__tabstops">
                <div class="lp-ruler__tabstop"
                     ng-repeat="tabstop in vm.tabstops track by $index"
                     ng-style="{ left: vm.getTabstopPosition(tabstop) + 'pt' }"
                     ng-class="vm.getTabstopClasses(tabstop, $index)"
                     ng-mousedown="vm.handleTabstopMouseDown($event, tabstop, $index)"
                     ng-click="vm.handleTabstopClick($event, tabstop, $index)"
                     ng-dblclick="vm.handleTabstopDoubleClick($event, tabstop, $index)"
                     data-tabstop-index="{{ $index }}"
                     data-tabstop-position="{{ tabstop.position }}"
                     title="{{ vm.getTabstopTitle(tabstop) }}">
                  <span class="lp-ruler__tabstop-symbol">
                    <span ng-if="vm.shouldUseTabstopIcon(tabstop)" 
                          ng-bind-html="vm.getTabstopIconHtml(tabstop)"></span>
                    <span ng-if="!vm.shouldUseTabstopIcon(tabstop)">{{ vm.getTabstopSymbol(tabstop) }}</span>
                  </span>
                  <span class="lp-ruler__tabstop-leader" ng-show="tabstop.leader !== 'none'">{{ vm.getLeaderIndicator(tabstop.leader) }}</span>
                </div>
              </div>
              
              <!-- Context menu for tabstops -->
              <div class="lp-ruler__context-menu"
                   ng-show="vm.contextMenuVisible"
                   ng-style="{ left: vm.contextMenuX + 'px', top: vm.contextMenuY + 'px' }"
                   ng-click="$event.stopPropagation()">
                <div class="lp-ruler__context-menu-section">
                  <div class="lp-ruler__context-menu-title">Alignment</div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.alignment === 'left' }"
                       ng-click="vm.setTabstopAlignment('left')">
                    <span class="lp-ruler__context-menu-item-icon">L</span>
                    <span class="lp-ruler__context-menu-item-label">Left</span>
                  </div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.alignment === 'center' }"
                       ng-click="vm.setTabstopAlignment('center')">
                    <span class="lp-ruler__context-menu-item-icon">C</span>
                    <span class="lp-ruler__context-menu-item-label">Center</span>
                  </div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.alignment === 'right' }"
                       ng-click="vm.setTabstopAlignment('right')">
                    <span class="lp-ruler__context-menu-item-icon">R</span>
                    <span class="lp-ruler__context-menu-item-label">Right</span>
                  </div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.alignment === 'decimal' }"
                       ng-click="vm.setTabstopAlignment('decimal')">
                    <span class="lp-ruler__context-menu-item-icon">.</span>
                    <span class="lp-ruler__context-menu-item-label">Decimal</span>
                  </div>
                </div>
                <div class="lp-ruler__context-menu-divider"></div>
                <div class="lp-ruler__context-menu-section">
                  <div class="lp-ruler__context-menu-title">Leader</div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.leader === 'none' }"
                       ng-click="vm.setTabstopLeader('none')">
                    <span class="lp-ruler__context-menu-item-label">None</span>
                  </div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.leader === 'dots' }"
                       ng-click="vm.setTabstopLeader('dots')">
                    <span class="lp-ruler__context-menu-item-icon">·</span>
                    <span class="lp-ruler__context-menu-item-label">Dots</span>
                  </div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.leader === 'dashes' }"
                       ng-click="vm.setTabstopLeader('dashes')">
                    <span class="lp-ruler__context-menu-item-icon">-</span>
                    <span class="lp-ruler__context-menu-item-label">Dashes</span>
                  </div>
                  <div class="lp-ruler__context-menu-item"
                       ng-class="{ 'lp-ruler__context-menu-item--active': vm.contextMenuTabstop && vm.contextMenuTabstop.leader === 'underline' }"
                       ng-click="vm.setTabstopLeader('underline')">
                    <span class="lp-ruler__context-menu-item-icon">_</span>
                    <span class="lp-ruler__context-menu-item-label">Underline</span>
                  </div>
                </div>
                <div class="lp-ruler__context-menu-divider"></div>
                <div class="lp-ruler__context-menu-section">
                  <div class="lp-ruler__context-menu-item lp-ruler__context-menu-item--danger"
                       ng-click="vm.deleteTabstopFromContextMenu()">
                    <span class="lp-ruler__context-menu-item-label">Delete</span>
                  </div>
                </div>
              </div>
              
              <!-- Remove indicator (shown when dragging tabstop off ruler) -->
              <div class="lp-ruler__remove-indicator" 
                   ng-show="vm.isDragging && vm.isDragOffRuler"
                   ng-style="{ left: vm.dragPosition + 'px' }">
                ✕ Release to remove
              </div>
              
              <!-- Position indicator during drag -->
              <div class="lp-ruler__position-indicator" 
                   ng-show="vm.isDragging"
                   ng-style="{ left: vm.dragPosition + 'px' }">
                {{ vm.dragPositionPoints }}pt
              </div>
              
              <!-- Vertical guide line during drag -->
              <div class="lp-ruler__drag-guide"
                   ng-show="vm.isDragging"
                   ng-style="{ left: vm.dragPosition + 'px' }">
              </div>
            </div>
          </div>
        </div>
      `;
    }
    
    /**
     * Controller for the ruler directive
     */
    function RulerController($scope, $element) {
      const vm = this;
      
      // Visibility state: ruler is only in the DOM when user toggles it on (ng-if="isRuler"),
      // so when we're created we should start visible. loadVisibilityPreference() is still
      // used when saving on hide; in production it often returns false (fresh storage or
      // unavailable), which would cause the lease editor watch to set isRuler=false and
      // remove the ruler immediately if we used it here.
      vm.isVisible = true;
      
      // Ruler dimensions (in pixels)
      vm.rulerWidth = 0;
      vm.scaleWidth = 0;
      vm.contentLeft = 0; // Left position of content to align with document
      vm.editorScale = 1; // Visual/CSS scale factor for .lease--editor
      
      // Padding values (in pixels) - extracted from .lease.lease--editor
      vm.paddingLeft = 0;
      vm.paddingRight = 0;
      vm.paddingLeftPoints = 0;
      vm.paddingRightPoints = 0;
      
      // Content area dimensions (excluding padding)
      vm.contentWidth = 0; // Content area width in pixels
      vm.contentWidthPoints = 0; // Content area width in points
      
      // Compression ratio for tick generation (to fit full range into ruler width)
      vm.tickCompressionRatio = 1; // Default to 1:1 (no compression)
      vm.tickStartPoints = 0; // Logical start point for tick generation (-2.5 inches in points)
      vm.tick14PixelPosition = 0; // Pixel position of tick 14 (calculated in updateRuler)
      
      // Margin and indent positions (in pixels from ruler left)
      vm.leftMarginPosition = 0;
      vm.rightMarginPosition = 0;
      vm.firstLineIndentPosition = 0;
      vm.hangingIndentPosition = 0;
      
      // Margin and indent values (in points)
      vm.leftMarginPoints = DEFAULT_LEFT_MARGIN;
      vm.rightMarginPoints = DEFAULT_RIGHT_MARGIN;
      vm.firstLineIndentPoints = 0;
      vm.hangingIndentPoints = 0;
      
      // Flags for showing indent markers
      vm.hasFirstLineIndent = false;
      vm.hasHangingIndent = false;
      vm.hasRightIndent = false;
      
      // Right indent position (in pixels from ruler right edge)
      vm.rightIndentPosition = 0;
      // Right indent value (in points - margin-right)
      vm.rightIndentPoints = 0;
      
      // Current paragraph's tabstops
      vm.tabstops = [];

      // Current active paragraph
      vm.activeParagraph = null;

      // Current active nested paragraph (when cursor is inside a nested <p data-nested-paragraph-index>)
      vm.activeNestedParagraph = null;

      // Parent base indentation offsets (in points) when inside a nested paragraph
      // These represent the parent's contribution that the nested paragraph's indentation is relative to
      vm.parentBaseMarginLeft = 0;
      vm.parentBaseMarginRight = 0;

      // Multi-paragraph selection state
      vm.selectedParagraphs = [];
      vm.isMultiParagraphSelection = false;
      vm.selectedParagraphCount = 0;

      // Partial tabstops (exist in some but not all selected paragraphs)
      vm.partialTabstops = [];

      // Index of tabstop that just changed (for animation)
      vm.changedTabstopIndex = -1;
      
      // Context menu state
      vm.contextMenuVisible = false;
      vm.contextMenuX = 0;
      vm.contextMenuY = 0;
      vm.contextMenuTabstop = null;
      vm.contextMenuTabstopIndex = -1;
      vm.contextMenuCloseHandler = null;
      vm.contextMenuEscapeHandler = null;
      
      // Tick marks HTML (pre-rendered for performance)
      vm.ticksHtml = $sce.trustAsHtml('');
      
      // Drag state
      vm.isDragging = false;
      vm.dragTabstopIndex = -1;
      vm.dragTabstop = null;
      vm.dragPosition = 0; // Current drag position in pixels
      vm.dragPositionPoints = 0; // Current drag position in points
      vm.dragStartPosition = 0; // Original position before drag
      vm.dragScaleElement = null; // Reference to scale element for bounds
      vm.justFinishedDragging = false; // Flag to prevent click after drag
      vm.isDragOffRuler = false; // Flag indicating tabstop is being dragged off ruler (for removal)
      vm.dragBeforeState = null; // State captured at drag start for undo support
      vm.dragTargetParagraphs = null; // Paragraphs being modified during drag

      // Indent drag state
      vm.isDraggingIndent = false;
      vm.dragIndentType = null; // 'first-line' or 'hanging'
      vm.dragIndentPosition = 0; // Current drag position in pixels
      vm.dragIndentPoints = 0; // Current drag position in points
      vm.dragIndentStartPosition = 0; // Original position before drag
      vm.dragIndentScaleElement = null; // Reference to scale element for bounds
      vm.dragIndentBeforeState = null; // State captured at drag start for undo
      vm.dragIndentTargetParagraphs = null; // Paragraphs being modified
      vm.dragIndentFixedPosition = 0; // Fixed indent position (first-line when dragging hanging, hanging when dragging first-line)
      vm.dragIndentFixedVisualPosition = 0; // Fixed visual position in points for display (first-line indicator when dragging hanging)
      vm.dragIndentMoveBothTogether = false; // Flag: when both indicators are at 0, dragging hanging moves both together
      vm.dragIndentParentBaseLeft = 0; // Parent base margin-left at drag start (for nested paragraphs)
      vm.dragIndentParentBaseRight = 0; // Parent base margin-right at drag start (for nested paragraphs)
      
      // Resize handler state (for debouncing)
      vm.resizeTimeout = null;
      vm.resizeHandler = null;
      
      // Drag safeApply debouncing state
      vm.dragSafeApplyTimeout = null;
      
      /**
       * Debounced safeApply for drag operations to improve performance
       * Limits safeApply calls to ~60fps during drag operations
       */
      function debouncedSafeApply() {
        if (vm.dragSafeApplyTimeout) {
          $timeout.cancel(vm.dragSafeApplyTimeout);
        }
        vm.dragSafeApplyTimeout = $timeout(function() {
          $scope.safeApply();
          vm.dragSafeApplyTimeout = null;
        }, DRAG_SAFEAPPLY_DEBOUNCE_MS);
      }
      
      /**
       * Standardized error handling utility
       * @param {Error|string} error - The error object or message
       * @param {string} context - Context description for debugging
       * @param {string} userMessage - Optional user-friendly error message
       */
      function handleError(error, context, userMessage) {
        // Log to console in development
        if (window.CONSTS && window.CONSTS.DEBUG) {
          console.error('lpRuler Error:', context, error);
        } else {
          // In production, use warn for non-critical errors
          console.warn('lpRuler:', context, error instanceof Error ? error.message : error);
        }
        
        // Log to error tracking service if available
        if (window.track && window.track.error) {
          window.track.error(error, { context: context });
        }
        
        // Show user-friendly message if provided
        if (userMessage && $rootScope.$broadcast) {
          $rootScope.$broadcast('error:show', { message: userMessage });
        }
      }
      
      /**
       * Loads visibility preference from local storage
       */
      function loadVisibilityPreference() {
        try {
          const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
          return stored === 'true';
        } catch (e) {
          handleError(e, 'loadVisibilityPreference', null);
          return false; // Default to hidden
        }
      }
      
      /**
       * Saves visibility preference to local storage
       */
      function saveVisibilityPreference(isVisible) {
        try {
          localStorage.setItem(LOCAL_STORAGE_KEY, isVisible ? 'true' : 'false');
        } catch (e) {
          handleError(e, 'saveVisibilityPreference', null);
          // Ignore storage errors - not critical
        }
      }
      
      /**
       * Toggles ruler visibility
       */
      vm.toggleVisibility = function() {
        vm.isVisible = !vm.isVisible;
        saveVisibilityPreference(vm.isVisible);

        if (vm.isVisible) {
          // Refresh ruler when shown
          $timeout(function() {
            vm.updateRuler();
          }, 0);
        }

        // Track analytics
        if (window.track && window.track.event) {
          window.track.event(new RulerToggledEvent({
            visible: vm.isVisible,
            context: $rootScope.getContext ? $rootScope.getContext() : {}
          }));
        }
      };
      
      /**
       * Shows the ruler
       */
      vm.show = function() {
        if (!vm.isVisible) {
          vm.isVisible = true;
          saveVisibilityPreference(true);
          $timeout(function() {
            vm.updateRuler();
          }, 0);
        }
      };
      
      /**
       * Hides the ruler
       */
      vm.hide = function() {
        if (vm.isVisible) {
          vm.isVisible = false;
          saveVisibilityPreference(false);
        }
      };
      
      /**
       * Gets the style for the content element (positions it to align with document)
       */
      vm.getContentStyle = function() {
        return {
          left: (vm.contentLeft || 0) + 'px',
          width: (vm.scaleWidth || (8.5 * 96)) + 'px' // Default to 8.5in in pixels if not set
        };
      };
      
      /**
       * Gets the style for the scale element
       */
      vm.getScaleStyle = function() {
        return {
          width: (vm.scaleWidth || (8.5 * 96)) + 'px', // Default to 8.5in in pixels
          height: RULER_HEIGHT + 'px'
        };
      };
      
      /**
       * Gets the visual position for the first-line indicator
       * When dragging hanging, keeps the indicator visually fixed at its original position
       */
      vm.getFirstLineIndentPosition = function() {
        if (vm.isDraggingIndent && vm.dragIndentType === 'first-line') {
          return vm.dragIndentPosition;
        } else if (vm.isDraggingIndent && vm.dragIndentType === 'hanging') {
          return vm.dragIndentFixedVisualPosition;
        } else {
          return vm.firstLineIndentPosition;
        }
      };
      
      /**
       * Generates tick marks HTML for the ruler scale
       * @param {number} contentWidthInPoints - Width of the content area in points (excluding padding)
       * @param {number} paddingLeftPoints - Left padding in points
       * @param {number} paddingRightPoints - Right padding in points
       * @returns {string} - HTML for tick marks
       */
      function generateTicksHtml(contentWidthInPoints, paddingLeftPoints, paddingRightPoints) {
        let html = '';
        const visualScale = vm.editorScale || 1;
        
        // Define the range we want to display: -2.5 to 18.5 inches (or content width if larger)
        const startInches = -2.5;
        const contentWidthInches = contentWidthInPoints / POINTS_PER_INCH;
        const endInches = Math.max(contentWidthInches, 18.5); // Show at least up to 18.5, or content width if larger
        
        // Convert to points
        const startPoints = startInches * POINTS_PER_INCH; // -180 points
        const endPoints = endInches * POINTS_PER_INCH;
        const totalLogicalPoints = endPoints - startPoints; // Total logical range in points
        
        // Total ruler width includes padding zones (forbidden zones on left and right)
        const totalRulerWidthPoints = contentWidthInPoints + paddingLeftPoints + paddingRightPoints;
        
        // Calculate compression ratio to fit the full range into the total ruler width (including padding)
        // This compresses the scale so -2.5 to endInches fits into totalRulerWidthPoints
        // Negative ticks will appear in the left padding zone, positive ticks in content + right padding
        const compressionRatio = totalRulerWidthPoints / totalLogicalPoints;
        
        // Generate ticks at intervals matching STEP_SIZE/4 to align with snapping
        // This ensures indicators snap to tick positions
        const tickInterval = getTickInterval();
        // Ticks: -2.5, -2.25, -2, -1.75, -1.5, -1.25, -1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, ... up to end
        for (let logicalPt = startPoints; logicalPt <= endPoints; logicalPt += tickInterval) {
          // Calculate compressed position: map logical point to compressed position within total ruler width
          const relativeLogicalPt = logicalPt - startPoints; // Position relative to -2.5 start
          const compressedPt = relativeLogicalPt * compressionRatio;
          
          // Convert to pixels - ticks are positioned from 0 (left edge of ruler) to totalRulerWidth
          const px = TabRenderer.pointsToPixels(compressedPt) * visualScale;
          
          // Only render ticks that would be visible (within reasonable bounds)
          const totalWidthPixels = TabRenderer.pointsToPixels(totalRulerWidthPoints) * visualScale;
          if (px < -50 || px > totalWidthPixels + 50) {
            continue;
          }
          
          // Calculate the logical inches for labeling
          const logicalInches = logicalPt / POINTS_PER_INCH;
          
          // Classify ticks: major (whole numbers), minor (half numbers), small (quarter numbers)
          const remainder = Math.abs(logicalInches % 1);
          const isWholeNumber = remainder < 0.001; // Major tick at whole inches
          const isHalfNumber = Math.abs(remainder - 0.5) < 0.001; // Minor tick at half inches
          
          let tickClass = 'lp-ruler__tick';
          if (isWholeNumber) {
            tickClass += ' lp-ruler__tick--major';
          } else if (isHalfNumber) {
            tickClass += ' lp-ruler__tick--minor';
          } else {
            tickClass += ' lp-ruler__tick--small';
          }
          
          html += '<div class="' + tickClass + '" style="left: ' + px + 'px;">';
          
          // Add label for whole number ticks (including 0 and negative numbers)
          if (isWholeNumber) {
            // Format: show as integer if whole number
            const formattedInches = Math.round(logicalInches);
            html += '<span class="lp-ruler__tick-label">' + formattedInches + '</span>';
          }
          
          html += '</div>';
        }
        
        return html;
      }
      
      /**
       * Converts a position in points (relative to content start) to points position on ruler
       * Indicators use points directly, maintaining the base offset in points.
       * @param {number} pointsOffset - Position in points relative to content area start (0 = content start)
       * @returns {number} - Position in points from ruler left edge
       */
      function pointsToRulerPoints(pointsOffset) {
        // Map logical points to visual ruler points (accounts for editor transform scale).
        const visualScale = vm.editorScale || 1;
        return (vm.paddingLeftPoints + pointsOffset) * visualScale;
      }
      
      /**
       * Converts a points position on ruler to points offset (relative to content start)
       * Reverse of pointsToRulerPoints
       * @param {number} pointsPosition - Position in points from ruler left edge
       * @returns {number} - Position in points relative to content area start (0 = content start)
       */
      function rulerPointsToOffset(pointsPosition) {
        const visualScale = vm.editorScale || 1;
        // Inverse of pointsToRulerPoints.
        return (pointsPosition / visualScale) - vm.paddingLeftPoints;
      }
      
      /**
       * Converts a pixel position on ruler to points offset (relative to content start)
       * Used when dragging from pixel coordinates
       * @param {number} pixelPosition - Position in pixels from ruler left edge
       * @returns {number} - Position in points relative to content area start (0 = content start)
       */
      function rulerPixelsToPoints(pixelPosition) {
        // Convert pixel position to points, then get offset
        const pointsPosition = TabRenderer.pixelsToPoints(pixelPosition);
        return rulerPointsToOffset(pointsPosition);
      }
      
      /**
       * Gets the pixel position for a tabstop marker
       * @param {Object} tabstop - The tabstop object
       * @returns {number} - Position in pixels (using compression-aware calculation)
       */
      vm.getTabstopPosition = function(tabstop) {
        // Tabstop position is in points relative to content area start
        // Convert to points position for display (tabstops also use points now)
        return pointsToRulerPoints(tabstop.position);
      };
      
      /**
       * Gets the alignment symbol for a tabstop
       * @param {Object} tabstop - The tabstop object
       * @returns {string} - Symbol character
       */
      vm.getTabstopSymbol = function(tabstop) {
        return ALIGNMENT_SYMBOLS[tabstop.alignment] || ALIGNMENT_SYMBOLS.left;
      };
      
      /**
       * Gets the inline SVG icon HTML for a tabstop
       * @param {Object} tabstop - The tabstop object
       * @returns {Object} - Trusted HTML with inline SVG or null if should use text
       */
      vm.getTabstopIconHtml = function(tabstop) {
        const alignment = tabstop.alignment || 'left';
        // Use icon for left, right, center; use text for decimal
        if (alignment === 'decimal') {
          return null;
        }
        const svgHtml = ALIGNMENT_SVG_ICONS[alignment] || ALIGNMENT_SVG_ICONS.left;
        return $sce.trustAsHtml(svgHtml);
      };
      
      /**
       * Checks if tabstop should use icon or text
       * @param {Object} tabstop - The tabstop object
       * @returns {boolean} - True if should use icon
       */
      vm.shouldUseTabstopIcon = function(tabstop) {
        return tabstop.alignment !== 'decimal';
      };
      
      /**
       * Gets the leader indicator for a tabstop
       * @param {string} leader - Leader style
       * @returns {string} - Indicator character
       */
      vm.getLeaderIndicator = function(leader) {
        switch (leader) {
          case 'dots': return '·';
          case 'dashes': return '-';
          case 'underline': return '_';
          default: return '';
        }
      };
      
      /**
       * Gets the tooltip title for a tabstop
       * @param {Object} tabstop - The tabstop object
       * @returns {string} - Tooltip text
       */
      vm.getTabstopTitle = function(tabstop) {
        let title = tabstop.alignment.charAt(0).toUpperCase() + tabstop.alignment.slice(1);
        title += ' tab at ' + tabstop.position + 'pt';
        if (tabstop.leader && tabstop.leader !== 'none') {
          title += ' (' + tabstop.leader + ' leader)';
        }
        if (vm.isMultiParagraphSelection) {
          title += '\n[Common to all ' + vm.selectedParagraphCount + ' selected paragraphs]';
        }
        title += '\nRight-click: context menu';
        title += '\nClick: change alignment';
        title += '\nShift+Click: change leader';
        title += '\nDouble-click: remove';
        title += '\nDrag: move position';
        return title;
      };

      /**
       * Gets the tooltip title for a partial tabstop (not in all selected paragraphs)
       * @param {Object} tabstop - The tabstop object
       * @returns {string} - Tooltip text
       */
      vm.getPartialTabstopTitle = function(tabstop) {
        let title = tabstop.alignment.charAt(0).toUpperCase() + tabstop.alignment.slice(1);
        title += ' tab at ' + tabstop.position + 'pt';
        if (tabstop.leader && tabstop.leader !== 'none') {
          title += ' (' + tabstop.leader + ' leader)';
        }
        title += '\n[In ' + tabstop.paragraphCount + ' of ' + vm.selectedParagraphCount + ' selected paragraphs]';
        title += '\nClick on ruler to add to all';
        return title;
      };
      
      /**
       * Gets the hanging indent position (current or drag position)
       * @returns {number} - Position in points
       */
      vm.getHangingIndentPosition = function() {
        if (vm.isDraggingIndent && (vm.dragIndentType === 'hanging' || vm.dragIndentType === 'hanging-ticker')) {
          return vm.dragIndentPosition;
        }
        return vm.hangingIndentPosition;
      };
      
      /**
       * Gets the hanging indent points (current or drag points)
       * @returns {number} - Points value
       */
      vm.getHangingIndentPoints = function() {
        if (vm.isDraggingIndent && (vm.dragIndentType === 'hanging' || vm.dragIndentType === 'hanging-ticker')) {
          return vm.dragIndentPoints;
        }
        return vm.hangingIndentPoints;
      };
      
      /**
       * Gets the hanging ticker indent position (current or drag position)
       * @returns {number} - Position in points
       */
      vm.getHangingTickerIndentPosition = function() {
        if (vm.isDraggingIndent && vm.dragIndentType === 'hanging-ticker') {
          return vm.dragIndentPosition;
        }
        return vm.hangingIndentPosition;
      };
      
      /**
       * Gets the right indent position (current or drag position)
       * @returns {number} - Position in pixels
       */
      vm.getRightIndentPosition = function() {
        if (vm.isDraggingIndent && vm.dragIndentType === 'right-indent') {
          return vm.dragIndentPosition;
        }
        return vm.rightIndentPosition;
      };
      
      /**
       * Gets the right indent points (current or drag points)
       * @returns {number} - Points value
       */
      vm.getRightIndentPoints = function() {
        if (vm.isDraggingIndent && vm.dragIndentType === 'right-indent') {
          return vm.dragIndentPoints;
        }
        return vm.rightIndentPoints;
      };
      
      /**
       * Gets the first line indent points (current or drag points)
       * @returns {number} - Points value
       */
      vm.getFirstLineIndentPoints = function() {
        if (vm.isDraggingIndent && vm.dragIndentType === 'first-line') {
          return vm.dragIndentPoints;
        }
        return vm.firstLineIndentPoints;
      };
      
      /**
       * Gets the CSS classes object for a tabstop
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       * @returns {Object} - Class object for ng-class
       */
      vm.getTabstopClasses = function(tabstop, index) {
        return {
          'lp-ruler__tabstop--left': tabstop.alignment === 'left',
          'lp-ruler__tabstop--right': tabstop.alignment === 'right',
          'lp-ruler__tabstop--center': tabstop.alignment === 'center',
          'lp-ruler__tabstop--decimal': tabstop.alignment === 'decimal',
          'lp-ruler__tabstop--dragging': vm.isDragging && vm.dragTabstopIndex === index,
          'lp-ruler__tabstop--removing': vm.isDragging && vm.dragTabstopIndex === index && vm.isDragOffRuler,
          'lp-ruler__tabstop--changed': vm.changedTabstopIndex === index
        };
      };
      
      /**
       * Gets the CSS classes object for a partial tabstop
       * @param {Object} tabstop - The tabstop object
       * @returns {Object} - Class object for ng-class
       */
      vm.getPartialTabstopClasses = function(tabstop) {
        return {
          'lp-ruler__tabstop--left': tabstop.alignment === 'left',
          'lp-ruler__tabstop--right': tabstop.alignment === 'right',
          'lp-ruler__tabstop--center': tabstop.alignment === 'center',
          'lp-ruler__tabstop--decimal': tabstop.alignment === 'decimal'
        };
      };
      
      /**
       * Updates the ruler to reflect current document/paragraph state
       */
      vm.updateRuler = function() {
        // Find the lease editor container to get document dimensions
        const leaseEditor = document.querySelector('.lease.lease--editor');
        if (!leaseEditor) {
          return;
        }
        
        // Get the document's position relative to the viewport
        const editorRect = leaseEditor.getBoundingClientRect();
        const rulerRect = $element[0].firstElementChild.getBoundingClientRect();
        
        // Get computed styles for padding (extracted from .lease.lease--editor)
        const editorStyle = window.getComputedStyle(leaseEditor);
        const rawPaddingLeft = parseFloat(editorStyle.paddingLeft) || DEFAULT_LEFT_MARGIN * TabRenderer.POINTS_TO_PIXELS;
        const rawPaddingRight = parseFloat(editorStyle.paddingRight) || DEFAULT_RIGHT_MARGIN * TabRenderer.POINTS_TO_PIXELS;

        // Compute visual scale from rendered width vs CSS layout width.
        const cssWidth = parseFloat(editorStyle.width) || leaseEditor.offsetWidth || editorRect.width;
        vm.editorScale = cssWidth > 0 ? (editorRect.width / cssWidth) : 1;

        // Keep points in logical (unscaled) coordinates for tabstop data.
        vm.paddingLeftPoints = TabRenderer.pixelsToPoints(rawPaddingLeft);
        vm.paddingRightPoints = TabRenderer.pixelsToPoints(rawPaddingRight);

        // Use visual padding in pixels for pointer constraints and ruler layout.
        vm.paddingLeft = rawPaddingLeft * vm.editorScale;
        vm.paddingRight = rawPaddingRight * vm.editorScale;
        
        // Calculate the left position to align ruler's left edge with document's left edge
        // Round to avoid sub-pixel positioning issues
        vm.contentLeft = Math.round(editorRect.left - rulerRect.left);
        
        // Use the actual bounding rect width which accounts for transform scale
        // This ensures the ruler matches the document's rendered width exactly
        vm.scaleWidth = editorRect.width;
        vm.rulerWidth = editorRect.width;
        
        // Calculate content area width (excluding padding)
        vm.contentWidth = vm.scaleWidth - vm.paddingLeft - vm.paddingRight;
        vm.contentWidthPoints = TabRenderer.pixelsToPoints(vm.contentWidth / (vm.editorScale || 1));
        
        // Calculate compression ratio for tick generation (same as in generateTicksHtml)
        const startInches = -2.5;
        const contentWidthInches = vm.contentWidthPoints / POINTS_PER_INCH;
        const endInches = Math.max(contentWidthInches, 18.5);
        vm.tickStartPoints = startInches * POINTS_PER_INCH; // -180 points
        const endPoints = endInches * POINTS_PER_INCH;
        const totalLogicalPoints = endPoints - vm.tickStartPoints;
        const totalRulerWidthPoints = vm.contentWidthPoints + vm.paddingLeftPoints + vm.paddingRightPoints;
        vm.tickCompressionRatio = totalRulerWidthPoints / totalLogicalPoints;
        
        // Calculate and store the pixel position of tick 14 (for constraint, like left margin)
        // Use the EXACT same method as tick generation
        const tick14LogicalPt = vm.tickStartPoints + (MAX_TICK - startInches) * POINTS_PER_INCH;
        const tick14RelativeLogicalPt = tick14LogicalPt - vm.tickStartPoints;
        const tick14CompressedPt = tick14RelativeLogicalPt * vm.tickCompressionRatio;
        vm.tick14PixelPosition = TabRenderer.pointsToPixels(tick14CompressedPt) * (vm.editorScale || 1);
        
        // Calculate and store the pixel position of tick 6 (for right indent constraint)
        // Use the EXACT same method as tick generation
        const tick6LogicalPt = vm.tickStartPoints + (MIN_TICK - startInches) * POINTS_PER_INCH;
        const tick6RelativeLogicalPt = tick6LogicalPt - vm.tickStartPoints;
        const tick6CompressedPt = tick6RelativeLogicalPt * vm.tickCompressionRatio;
        vm.tick6PixelPosition = TabRenderer.pointsToPixels(tick6CompressedPt) * (vm.editorScale || 1);
        
        // Generate tick marks for the content area (excluding padding)
        // Pass padding info so ticks are offset correctly
        vm.ticksHtml = $sce.trustAsHtml(generateTicksHtml(vm.contentWidthPoints, vm.paddingLeftPoints, vm.paddingRightPoints));
        
        // Update margin positions (relative to scale start)
        // Margin indicators should be positioned at the document's padding boundaries
        vm.leftMarginPoints = vm.paddingLeftPoints;
        vm.rightMarginPoints = vm.paddingRightPoints;
        // Position margin indicators at the padding boundaries within the ruler scale
        vm.leftMarginPosition = vm.paddingLeft; // Left padding position (uses 'left' positioning)
        vm.rightMarginPosition = vm.paddingRight; // Right padding position (uses 'right' positioning)
        
        // Update tabstops for active paragraph
        vm.updateTabstopsForParagraph(vm.activeParagraph);
      };
      
      /**
       * Handles click on the ruler scale (not on tabstop markers)
       * Adds a new tabstop at the clicked position
       * @param {Event} event - The click event
       */
      vm.handleRulerClick = function(event) {
        // Don't add if no paragraph is active
        if (!vm.activeParagraph) {
          handleError('No active paragraph to add tabstop', 'handleRulerClick', null);
          return;
        }
        
        // Calculate position in points from click coordinates
        const scaleElement = event.currentTarget.parentElement;
        const scaleRect = scaleElement.getBoundingClientRect();
        const clickXPixels = event.clientX - scaleRect.left;
        
        // Constrain to content area (exclude padding zones)
        // Content area starts at paddingLeft and ends at scaleWidth - paddingRight
        const minPixelPosition = vm.paddingLeft;
        const maxPixelPosition = vm.scaleWidth - vm.paddingRight;
        const constrainedPixelPosition = Math.max(minPixelPosition, Math.min(clickXPixels, maxPixelPosition));
        
        // Convert from compressed ruler pixel position to logical document points
        // The ruler uses compression, so we need to reverse it:
        // 1. Convert pixel to compressed points
        // 2. Reverse compression to get logical points
        // 3. Find where logical point 0 is (content start) and calculate offset
        // Convert from ruler pixel position to points offset (relative to content start)
        // Use the SAME reverse calculation as tick generation
        const contentAreaOffsetPoints = rulerPixelsToPoints(constrainedPixelPosition);
        
        // Snap to the same grid used during drag operations.
        let positionPoints = snapToQuarterStepSize(contentAreaOffsetPoints);
        if (positionPoints > MAX_POSITION_POINTS) {
          positionPoints = MAX_POSITION_POINTS;
        }
        positionPoints = Math.max(0, positionPoints);
        
        // Get selected paragraphs for multi-paragraph support
        const selectedParagraphs = getSelectedParagraphs();
        const targetParagraphs = selectedParagraphs.length > 0 ? selectedParagraphs : [vm.activeParagraph];
        
        // Create new tabstop with default alignment and leader
        const newTabstop = {
          position: positionPoints,
          alignment: 'left',
          leader: 'none'
        };

        // Capture state before modification for undo support
        const beforeState = captureTabstopState(targetParagraphs);

        // Add tabstop to all target paragraphs
        let addedCount = 0;
        targetParagraphs.forEach(function(paragraph) {
          if (addTabstopToParagraph(paragraph, newTabstop)) {
            addedCount++;
          }
        });

        if (addedCount > 0) {
          // Capture state after modification for redo support
          const afterState = captureTabstopState(targetParagraphs);

          // Broadcast change event to trigger re-rendering and history registration
          $rootScope.$broadcast('tabstops:changed', {
            paragraph: vm.activeParagraph,
            action: 'add',
            tabstop: newTabstop,
            paragraphCount: addedCount,
            beforeState: beforeState,
            afterState: afterState,
            restoreState: restoreTabstopState
          });
          
          // Re-render tabs in affected paragraphs
          targetParagraphs.forEach(function(paragraph) {
            TabRenderer.renderTabsInParagraph(paragraph);
          });
          
          // Track analytics
          if (window.track && window.track.event) {
            window.track.event(new TabstopAddedEvent({
              position: positionPoints,
              alignment: 'left',
              paragraphCount: addedCount,
              context: $rootScope.getContext ? $rootScope.getContext() : {}
            }));
          }
        }
      };
      
      // Alignment cycle order
      const ALIGNMENT_CYCLE = ['left', 'center', 'right', 'decimal'];
      
      // Leader style cycle order
      const LEADER_CYCLE = ['none', 'dots', 'dashes', 'underline'];
      
      /**
       * Updates a tabstop property (alignment or leader) across multiple paragraphs
       * Handles state capture, UI updates, event broadcasting, and analytics
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       * @param {string} property - The property to update ('alignment' or 'leader')
       * @param {string} newValue - The new value
       * @param {string} oldValue - The old value (for analytics)
       * @param {string} action - The action name ('change-alignment' or 'change-leader')
       * @param {Function} EventClass - The analytics event class constructor
       */
      function updateTabstopPropertyInParagraphs(tabstop, index, property, newValue, oldValue, action, EventClass) {
        // Get selected paragraphs for multi-paragraph support
        const selectedParagraphs = getSelectedParagraphs();
        const targetParagraphs = selectedParagraphs.length > 0 ? selectedParagraphs : [vm.activeParagraph];

        // Capture state before modification for undo support
        const beforeState = captureTabstopState(targetParagraphs);

        // Update tabstop property in all target paragraphs
        // Pass alignment to help match the correct tabstop (more precise than position alone)
        // Use current alignment for matching to identify the specific tabstop we're updating
        let updatedCount = 0;
        targetParagraphs.forEach(function(paragraph) {
          if (updateTabstopProperty(paragraph, tabstop.position, property, newValue, tabstop.alignment)) {
            updatedCount++;
          }
        });

        if (updatedCount > 0) {
          // Capture state after modification for redo support
          const afterState = captureTabstopState(targetParagraphs);

          // Update the local tabstop object for immediate UI feedback
          tabstop[property] = newValue;

          // Trigger animation feedback
          vm.changedTabstopIndex = index;
          $timeout(function() {
            vm.changedTabstopIndex = -1;
          }, ANIMATION_DURATION_MS);

          // Broadcast change event
          const eventData = {
            paragraph: vm.activeParagraph,
            action: action,
            tabstop: tabstop,
            paragraphCount: updatedCount,
            beforeState: beforeState,
            afterState: afterState,
            restoreState: restoreTabstopState
          };
          
          // Add property-specific fields
          if (property === 'alignment') {
            eventData.oldAlignment = oldValue;
            eventData.newAlignment = newValue;
          } else {
            eventData.oldLeader = oldValue;
            eventData.newLeader = newValue;
          }
          
          $rootScope.$broadcast('tabstops:changed', eventData);
          
          // Re-render tabs in affected paragraphs after DOM updates
          // Use $timeout to ensure the data-tabstops attribute has been updated in the DOM
          $timeout(function() {
            targetParagraphs.forEach(function(paragraph) {
              TabRenderer.renderTabsInParagraph(paragraph);
            });
          }, 0);
          
          // Track analytics
          if (window.track && window.track.event) {
            const analyticsData = {
              position: tabstop.position,
              paragraphCount: updatedCount,
              context: $rootScope.getContext ? $rootScope.getContext() : {}
            };
            
            if (property === 'alignment') {
              analyticsData.oldAlignment = oldValue;
              analyticsData.newAlignment = newValue;
            } else {
              analyticsData.oldLeader = oldValue;
              analyticsData.newLeader = newValue;
            }
            
            window.track.event(new EventClass(analyticsData));
          }
        }
      }
      
      /**
       * Handles click on an existing tabstop marker
       * - Click: cycles through alignment (left → center → right → decimal)
       * - Shift+Click: cycles through leader styles (none → dots → dashes → underline)
       * @param {Event} event - The click event
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       */
      vm.handleTabstopClick = function(event, tabstop, index) {
        // Stop event propagation to prevent ruler click handler
        event.stopPropagation();
        
        // If we just finished dragging, don't trigger click action
        if (vm.justFinishedDragging) {
          vm.justFinishedDragging = false;
          return;
        }
        
        // Don't process if no active paragraph
        if (!vm.activeParagraph) {
          handleError('No active paragraph to modify tabstop', 'handleTabstopClick', null);
          return;
        }
        
        // Determine which property to cycle based on modifier keys
        const cycleLeader = event.shiftKey;
        
        if (cycleLeader) {
          // Shift+Click: cycle leader styles
          cycleTabstopLeader(tabstop, index);
        } else {
          // Click: cycle alignment
          cycleTabstopAlignment(tabstop, index);
        }
      };
      
      /**
       * Cycles the alignment of a tabstop (left → center → right → decimal → left)
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       */
      function cycleTabstopAlignment(tabstop, index) {
        // Get current alignment index and compute next
        const currentAlignment = tabstop.alignment || 'left';
        const currentIndex = ALIGNMENT_CYCLE.indexOf(currentAlignment);
        const nextIndex = (currentIndex + 1) % ALIGNMENT_CYCLE.length;
        const nextAlignment = ALIGNMENT_CYCLE[nextIndex];

        updateTabstopPropertyInParagraphs(
          tabstop,
          index,
          'alignment',
          nextAlignment,
          currentAlignment,
          'change-alignment',
          TabstopAlignmentChangedEvent
        );
      }
      
      /**
       * Cycles the leader style of a tabstop (none → dots → dashes → underline → none)
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       */
      function cycleTabstopLeader(tabstop, index) {
        // Get current leader index and compute next
        const currentLeader = tabstop.leader || 'none';
        const currentIndex = LEADER_CYCLE.indexOf(currentLeader);
        const nextIndex = (currentIndex + 1) % LEADER_CYCLE.length;
        const nextLeader = LEADER_CYCLE[nextIndex];

        updateTabstopPropertyInParagraphs(
          tabstop,
          index,
          'leader',
          nextLeader,
          currentLeader,
          'change-leader',
          TabstopLeaderChangedEvent
        );
      }
      
      /**
       * Updates a property of a tabstop in a paragraph
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {number} position - The tabstop position in points
       * @param {string} property - The property to update ('alignment' or 'leader')
       * @param {string} value - The new value
       * @param {string} alignment - Optional: alignment to help match the correct tabstop (more precise matching)
       * @returns {boolean} - True if update was successful
       */
      function updateTabstopProperty(paragraph, position, property, value, alignment) {
        // Get existing tabstops
        const tabstops = TabstopService.parseTabstopsFromElement(paragraph);
        
        // Round positions to nearest point to handle floating point precision issues
        const roundedPosition = Math.round(position);
        
        // When changing alignment, match by position only (since we're changing the alignment)
        // For other properties (like leader), we can use alignment for more precise matching
        const shouldMatchByAlignment = alignment && property !== 'alignment';
        
        // Find the tabstop at the given position (within tolerance)
        // If alignment is provided and we're not changing alignment, also match by alignment for more precise matching
        const tabstop = tabstops.find(function(ts) {
          const roundedTabstopPosition = Math.round(ts.position);
          const positionMatch = Math.abs(roundedTabstopPosition - roundedPosition) <= TABSTOP_POSITION_TOLERANCE;
          if (shouldMatchByAlignment) {
            // Match by both position and alignment for more precision (when not changing alignment)
            return positionMatch && ts.alignment === alignment;
          }
          return positionMatch;
        });
        
        if (!tabstop) {
          // If alignment-based match failed, try position-only match as fallback
          const fallbackTabstop = tabstops.find(function(ts) {
            const roundedTabstopPosition = Math.round(ts.position);
            return Math.abs(roundedTabstopPosition - roundedPosition) <= TABSTOP_POSITION_TOLERANCE;
          });
          
          if (!fallbackTabstop) {
            handleError('Could not find tabstop at position ' + position + ' (rounded: ' + roundedPosition + ') in paragraph. Available positions: ' + JSON.stringify(tabstops.map(ts => ({ position: ts.position, rounded: Math.round(ts.position), alignment: ts.alignment }))), 'updateTabstopProperty', null);
            return false;
          }
          
          // Use fallback match (position-only)
          fallbackTabstop[property] = value;
          return TabstopService.setTabstopsOnElement(paragraph, tabstops);
        }
        
        // Update the property
        tabstop[property] = value;
        
        // Set tabstops back on paragraph
        return TabstopService.setTabstopsOnElement(paragraph, tabstops);
      }
      
      /**
       * Handles double-click on a tabstop marker to remove it
       * @param {Event} event - The double-click event
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       */
      vm.handleTabstopDoubleClick = function(event, tabstop, index) {
        // Stop event propagation
        event.stopPropagation();
        event.preventDefault();

        // Don't process if we're dragging
        if (vm.isDragging) {
          return;
        }

        // Don't process if no active paragraph
        if (!vm.activeParagraph) {
          handleError('No active paragraph to remove tabstop from', 'handleTabstopDoubleClick', null);
          return;
        }

        // Get selected paragraphs for multi-paragraph support
        const selectedParagraphs = getSelectedParagraphs();
        const targetParagraphs = selectedParagraphs.length > 0 ? selectedParagraphs : [vm.activeParagraph];

        // Capture state before modification for undo support
        const beforeState = captureTabstopState(targetParagraphs);

        // Remove tabstop from all target paragraphs
        let removedCount = 0;
        targetParagraphs.forEach(function(paragraph) {
          if (removeTabstopFromParagraph(paragraph, tabstop.position)) {
            removedCount++;
          }
        });

        if (removedCount > 0) {
          // Capture state after modification for redo support
          const afterState = captureTabstopState(targetParagraphs);

          // Broadcast change event
          $rootScope.$broadcast('tabstops:changed', {
            paragraph: vm.activeParagraph,
            action: 'remove',
            tabstop: tabstop,
            paragraphCount: removedCount,
            beforeState: beforeState,
            afterState: afterState,
            restoreState: restoreTabstopState
          });
          
          // Re-render tabs in affected paragraphs
          targetParagraphs.forEach(function(paragraph) {
            TabRenderer.renderTabsInParagraph(paragraph);
          });
          
          // Track analytics
          if (window.track && window.track.event) {
            window.track.event(new TabstopRemovedEvent({
              position: tabstop.position,
              alignment: tabstop.alignment,
              method: 'doubleclick',
              paragraphCount: removedCount,
              context: $rootScope.getContext ? $rootScope.getContext() : {}
            }));
          }
        }
      };
      
      /**
       * Closes the context menu and removes all event listeners
       */
      function closeContextMenu() {
        vm.contextMenuVisible = false;
        vm.contextMenuTabstop = null;
        vm.contextMenuTabstopIndex = -1;
        
        // Remove event listeners if they exist
        if (vm.contextMenuCloseHandler) {
          document.removeEventListener('click', vm.contextMenuCloseHandler);
          document.removeEventListener('contextmenu', vm.contextMenuCloseHandler);
          vm.contextMenuCloseHandler = null;
        }
        
        if (vm.contextMenuEscapeHandler) {
          document.removeEventListener('keydown', vm.contextMenuEscapeHandler);
          vm.contextMenuEscapeHandler = null;
        }
      }
      
      /**
       * Handles right-click on a tabstop marker to show context menu
       * @param {Event} event - The contextmenu event
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       */
      vm.handleTabstopContextMenu = function(event, tabstop, index) {
        // Prevent default browser context menu
        event.preventDefault();
        event.stopPropagation();

        // Don't show menu if we're dragging
        if (vm.isDragging) {
          return;
        }

        // Don't show menu if no active paragraph
        if (!vm.activeParagraph) {
          return;
        }

        // Close any existing menu first (clean up previous listeners)
        closeContextMenu();

        // Store the tabstop and index for context menu actions
        vm.contextMenuTabstop = tabstop;
        vm.contextMenuTabstopIndex = index;

        // Position the menu at the cursor location
        // Adjust if menu would go off-screen
        const menuWidth = 160; // min-width from CSS
        const menuHeight = 250; // approximate height
        let menuX = event.clientX;
        let menuY = event.clientY;
        
        // Adjust horizontal position if menu would go off right edge
        if (menuX + menuWidth > window.innerWidth) {
          menuX = window.innerWidth - menuWidth - 10;
        }
        
        // Adjust horizontal position if menu would go off left edge
        if (menuX < 10) {
          menuX = 10;
        }
        
        // Adjust vertical position if menu would go off bottom edge
        if (menuY + menuHeight > window.innerHeight) {
          menuY = window.innerHeight - menuHeight - 10;
        }
        
        // Adjust vertical position if menu would go off top edge
        if (menuY < 10) {
          menuY = 10;
        }
        
        vm.contextMenuX = menuX;
        vm.contextMenuY = menuY;

        // Show the menu
        vm.contextMenuVisible = true;

        // Apply changes to update UI
        $scope.safeApply();

        // Close menu when clicking outside or on another element
        // Use a timeout to avoid immediate closure from the right-click event
        $timeout(function() {
          // Only set up handlers if menu is still visible (scope might have been destroyed)
          if (!vm.contextMenuVisible) {
            return;
          }
          
          vm.contextMenuCloseHandler = function(e) {
            // Don't close if clicking inside the context menu
            if (e.target.closest('.lp-ruler__context-menu')) {
              return;
            }
            
            closeContextMenu();
            $scope.safeApply();
          };
          
          vm.contextMenuEscapeHandler = function(e) {
            if (e.key === 'Escape' || e.keyCode === 27) {
              closeContextMenu();
              $scope.safeApply();
            }
          };
          
          document.addEventListener('click', vm.contextMenuCloseHandler);
          document.addEventListener('contextmenu', vm.contextMenuCloseHandler);
          document.addEventListener('keydown', vm.contextMenuEscapeHandler);
        }, 0);
      };
      
      /**
       * Sets the alignment of the context menu tabstop
       * @param {string} alignment - The alignment value ('left', 'center', 'right', 'decimal')
       */
      vm.setTabstopAlignment = function(alignment) {
        if (!vm.contextMenuTabstop || !vm.activeParagraph) {
          closeContextMenu();
          return;
        }

        const tabstop = vm.contextMenuTabstop;
        const index = vm.contextMenuTabstopIndex;
        const oldAlignment = tabstop.alignment;

        // Close the menu and clean up listeners
        closeContextMenu();

        // Use consolidated update function
        updateTabstopPropertyInParagraphs(
          tabstop,
          index,
          'alignment',
          alignment,
          oldAlignment,
          'change-alignment',
          TabstopAlignmentChangedEvent
        );
      };
      
      /**
       * Sets the leader style of the context menu tabstop
       * @param {string} leader - The leader value ('none', 'dots', 'dashes', 'underline')
       */
      vm.setTabstopLeader = function(leader) {
        if (!vm.contextMenuTabstop || !vm.activeParagraph) {
          closeContextMenu();
          return;
        }

        const tabstop = vm.contextMenuTabstop;
        const index = vm.contextMenuTabstopIndex;
        const oldLeader = tabstop.leader;

        // Close the menu and clean up listeners
        closeContextMenu();

        // Use consolidated update function
        updateTabstopPropertyInParagraphs(
          tabstop,
          index,
          'leader',
          leader,
          oldLeader,
          'change-leader',
          TabstopLeaderChangedEvent
        );
      };
      
      /**
       * Deletes the tabstop from the context menu
       */
      vm.deleteTabstopFromContextMenu = function() {
        if (!vm.contextMenuTabstop || !vm.activeParagraph) {
          closeContextMenu();
          return;
        }

        const tabstop = vm.contextMenuTabstop;

        // Close the menu and clean up listeners
        closeContextMenu();

        // Get selected paragraphs for multi-paragraph support
        const selectedParagraphs = getSelectedParagraphs();
        const targetParagraphs = selectedParagraphs.length > 0 ? selectedParagraphs : [vm.activeParagraph];

        // Capture state before modification for undo support
        const beforeState = captureTabstopState(targetParagraphs);

        // Remove tabstop from all target paragraphs
        let removedCount = 0;
        targetParagraphs.forEach(function(paragraph) {
          if (removeTabstopFromParagraph(paragraph, tabstop.position)) {
            removedCount++;
          }
        });

        if (removedCount > 0) {
          // Capture state after modification for redo support
          const afterState = captureTabstopState(targetParagraphs);

          // Broadcast change event
          $rootScope.$broadcast('tabstops:changed', {
            paragraph: vm.activeParagraph,
            action: 'remove',
            tabstop: tabstop,
            paragraphCount: removedCount,
            beforeState: beforeState,
            afterState: afterState,
            restoreState: restoreTabstopState
          });
          
          // Re-render tabs in affected paragraphs
          targetParagraphs.forEach(function(paragraph) {
            TabRenderer.renderTabsInParagraph(paragraph);
          });
          
          // Track analytics
          if (window.track && window.track.event) {
            window.track.event(new TabstopRemovedEvent({
              position: tabstop.position,
              alignment: tabstop.alignment,
              method: 'context-menu',
              paragraphCount: removedCount,
              context: $rootScope.getContext ? $rootScope.getContext() : {}
            }));
          }
        }
      };
      
      /**
       * Handles mousedown on a tabstop marker to start dragging
       * @param {Event} event - The mousedown event
       * @param {Object} tabstop - The tabstop object
       * @param {number} index - The tabstop index
       */
      vm.handleTabstopMouseDown = function(event, tabstop, index) {
        // Only handle left mouse button
        if (event.button !== 0) {
          return;
        }

        // Preserve caret/selection before focus moves to the ruler
        if ($rootScope.saveSelection) {
          $rootScope.saveSelection(false);
        }
        
        // Prevent text selection during drag
        event.preventDefault();
        event.stopPropagation();
        
        // Get the scale element for bounds calculation
        const scaleElement = event.currentTarget.closest('.lp-ruler__scale');
        if (!scaleElement) {
          return;
        }
        
        // Get selected paragraphs for multi-paragraph support (capture at drag start)
        const selectedParagraphs = getSelectedParagraphs();
        vm.dragTargetParagraphs = selectedParagraphs.length > 0 ? selectedParagraphs : [vm.activeParagraph];

        // Capture state at drag start for undo support
        vm.dragBeforeState = captureTabstopState(vm.dragTargetParagraphs);

        // Initialize drag state
        vm.isDragging = true;
        vm.dragTabstopIndex = index;
        vm.dragTabstop = tabstop;
        vm.dragStartPosition = tabstop.position; // Position in points relative to content area
        vm.dragScaleElement = scaleElement;
        // Add padding offset for display on ruler
        vm.dragPosition = TabRenderer.pointsToPixels(tabstop.position) + vm.paddingLeft;
        vm.dragPositionPoints = tabstop.position;
        vm.justFinishedDragging = false;

        // Add document-level listeners for drag
        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
        
        // Add body class to prevent text selection
        document.body.classList.add('lp-ruler-dragging');
        
        // Apply changes to update UI
        $scope.safeApply();
      };
      
      /**
       * Handles mouse movement during drag
       * @param {Event} event - The mousemove event
       */
      function handleDragMove(event) {
        if (!vm.isDragging || !vm.dragScaleElement) {
          return;
        }
        
        // Calculate new position
        const scaleRect = vm.dragScaleElement.getBoundingClientRect();
        let newPixelPosition = event.clientX - scaleRect.left;
        
        // Check if cursor is outside the ruler vertically (for drag-off-to-remove)
        // Allow some tolerance (30px above or below the ruler)
        const dragOffThreshold = 30;
        const cursorY = event.clientY;
        const isAboveRuler = cursorY < scaleRect.top - dragOffThreshold;
        const isBelowRuler = cursorY > scaleRect.bottom + dragOffThreshold;
        vm.isDragOffRuler = isAboveRuler || isBelowRuler;
        
        // Constrain to content area (exclude padding zones)
        // Content area starts at paddingLeft and ends at tick 14 (like left margin, use stored pixel value)
        const minPosition = vm.paddingLeft; // Left margin constraint (works well)
        const maxPosition = vm.tick14PixelPosition; // Tick 14 constraint (same approach as left margin)
        newPixelPosition = Math.max(minPosition, Math.min(maxPosition, newPixelPosition));
        
        // Convert from ruler pixel position to points offset (relative to content start)
        // Use the SAME reverse calculation as tick generation
        let contentAreaOffsetPoints = rulerPixelsToPoints(newPixelPosition);
        
        // CRITICAL: Enforce MAX_POSITION_POINTS as absolute maximum BEFORE snapping
        // This ensures we never allow values beyond tick 14, even if calculations suggest otherwise
        if (contentAreaOffsetPoints > MAX_POSITION_POINTS) {
          contentAreaOffsetPoints = MAX_POSITION_POINTS;
        }
        
        // Snap to quarter step size and clamp to valid range (0 to MAX_POSITION_POINTS)
        // IMPORTANT: Enforce MAX_POSITION_POINTS as absolute maximum
        let newPointPosition = snapToQuarterStepSize(contentAreaOffsetPoints);
        // Clamp to ensure we never exceed tick 14 (1008 points = 14 inches)
        if (newPointPosition > MAX_POSITION_POINTS) {
          newPointPosition = MAX_POSITION_POINTS;
        }
        const clampedPointPosition = Math.max(0, Math.min(newPointPosition, MAX_POSITION_POINTS));
        
        // Update drag position (convert back to points for display)
        let displayPositionPoints = pointsToRulerPoints(clampedPointPosition);
        
        // Ensure position is within valid bounds:
        // - Never smaller than the left margin (paddingLeftPoints) - prevents going into forbidden zone
        // - Never larger than tick 14 position - prevents going beyond maximum
        // Convert tick 14 pixel position to points for comparison
        const maxDisplayPoints = TabRenderer.pixelsToPoints(vm.tick14PixelPosition);
        displayPositionPoints = Math.max(vm.paddingLeftPoints, Math.min(displayPositionPoints, maxDisplayPoints));
        
        vm.dragPosition = TabRenderer.pointsToPixels(displayPositionPoints); // Keep in pixels for tabstop drag (legacy)
        vm.dragPositionPoints = clampedPointPosition;
        
        // Update the tabstop's position in real-time for visual feedback
        if (vm.dragTabstopIndex >= 0 && vm.dragTabstopIndex < vm.tabstops.length) {
          vm.tabstops[vm.dragTabstopIndex].position = vm.dragPositionPoints;
        }
        
        // Apply changes to update UI
        $scope.safeApply();
      }
      
      /**
       * Removes tabstop from paragraphs when dragged off ruler
       * @param {number} originalPosition - Original tabstop position
       * @param {Array} targetParagraphs - Target paragraphs
       * @param {Object} beforeState - State before modification
       * @returns {number} - Number of paragraphs updated
       */
      function removeTabstopFromParagraphs(originalPosition, targetParagraphs, beforeState) {
        let removedCount = 0;
        const removedTabstop = vm.dragTabstop;
        
        targetParagraphs.forEach(function(paragraph) {
          if (removeTabstopFromParagraph(paragraph, originalPosition)) {
            removedCount++;
          }
        });

        if (removedCount > 0) {
          const afterState = captureTabstopState(targetParagraphs);
          
          broadcastTabstopChangeEvent('remove', {
            tabstop: removedTabstop,
            position: originalPosition,
            paragraphCount: removedCount,
            beforeState: beforeState,
            afterState: afterState
          });
          
          // Re-render tabs in affected paragraphs
          targetParagraphs.forEach(function(paragraph) {
            TabRenderer.renderTabsInParagraph(paragraph);
          });
        }
        
        return removedCount;
      }
      
      /**
       * Moves tabstop position in paragraphs
       * @param {number} originalPosition - Original tabstop position
       * @param {number} finalPosition - Final tabstop position
       * @param {Array} targetParagraphs - Target paragraphs
       * @param {Object} beforeState - State before modification
       * @returns {number} - Number of paragraphs updated
       */
      function moveTabstopInParagraphs(originalPosition, finalPosition, targetParagraphs, beforeState) {
        let updatedCount = 0;
        
        targetParagraphs.forEach(function(paragraph) {
          if (updateTabstopPosition(paragraph, originalPosition, finalPosition)) {
            updatedCount++;
          }
        });

        if (updatedCount > 0) {
          const afterState = captureTabstopState(targetParagraphs);
          
          broadcastTabstopChangeEvent('move', {
            oldPosition: originalPosition,
            newPosition: finalPosition,
            tabstop: vm.dragTabstop,
            paragraphCount: updatedCount,
            beforeState: beforeState,
            afterState: afterState
          });
          
          // Re-render tabs in affected paragraphs
          targetParagraphs.forEach(function(paragraph) {
            TabRenderer.renderTabsInParagraph(paragraph);
          });
        }
        
        return updatedCount;
      }
      
      /**
       * Broadcasts tabstop change event and tracks analytics
       * @param {string} action - Action type ('remove' or 'move')
       * @param {Object} data - Event data
       */
      function broadcastTabstopChangeEvent(action, data) {
        // Broadcast change event
        $rootScope.$broadcast('tabstops:changed', {
          paragraph: vm.activeParagraph,
          action: action,
          oldPosition: data.oldPosition,
          newPosition: data.newPosition,
          position: data.position,
          tabstop: data.tabstop,
          paragraphCount: data.paragraphCount,
          beforeState: data.beforeState,
          afterState: data.afterState,
          restoreState: restoreTabstopState
        });
        
        // Track analytics
        if (window.track && window.track.event) {
          if (action === 'remove') {
            window.track.event(new TabstopRemovedEvent({
              position: data.position,
              alignment: data.tabstop.alignment,
              method: 'drag-off',
              paragraphCount: data.paragraphCount,
              context: $rootScope.getContext ? $rootScope.getContext() : {}
            }));
          } else if (action === 'move') {
            window.track.event(new TabstopMovedEvent({
              oldPosition: data.oldPosition,
              newPosition: data.newPosition,
              paragraphCount: data.paragraphCount,
              context: $rootScope.getContext ? $rootScope.getContext() : {}
            }));
          }
        }
      }
      
      /**
       * Handles mouse up to end dragging
       * @param {Event} event - The mouseup event
       */
      function handleDragEnd(event) {
        if (!vm.isDragging) {
          return;
        }
        
        // Remove document-level listeners
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        
        // Remove body class
        document.body.classList.remove('lp-ruler-dragging');
        
        // Calculate final position (ensure it's snapped to quarter STEP_SIZE and clamped to max tick 14)
        let finalPosition = snapToQuarterStepSize(vm.dragPositionPoints);
        finalPosition = Math.min(finalPosition, MAX_POSITION_POINTS);
        const originalPosition = vm.dragStartPosition;
        const movedSignificantly = Math.abs(finalPosition - originalPosition) > 2;
        const wasDraggedOffRuler = vm.isDragOffRuler;
        
        // Flag that we just finished dragging (to suppress click event)
        vm.justFinishedDragging = movedSignificantly || wasDraggedOffRuler;
        
        const targetParagraphs = vm.dragTargetParagraphs || [vm.activeParagraph];
        const beforeState = vm.dragBeforeState;
        
        // Check if this is a drag-off-ruler removal action
        if (wasDraggedOffRuler && vm.activeParagraph && vm.dragTabstop) {
          removeTabstopFromParagraphs(originalPosition, targetParagraphs, beforeState);
        }
        // Only update position if actually moved (and not dragged off)
        else if (movedSignificantly && vm.activeParagraph) {
          moveTabstopInParagraphs(originalPosition, finalPosition, targetParagraphs, beforeState);
        } else {
          // Position didn't change - restore original
          if (vm.dragTabstopIndex >= 0 && vm.dragTabstopIndex < vm.tabstops.length) {
            vm.tabstops[vm.dragTabstopIndex].position = originalPosition;
          }
        }
        
        // Reset drag state
        vm.isDragging = false;
        vm.dragTabstopIndex = -1;
        vm.dragTabstop = null;
        vm.dragPosition = 0;
        vm.dragPositionPoints = 0;
        vm.dragStartPosition = 0;
        vm.dragScaleElement = null;
        vm.isDragOffRuler = false;
        vm.dragBeforeState = null;
        vm.dragTargetParagraphs = null;

        // Apply changes
        $scope.safeApply();

        // Restore caret/selection back to the document after drag completes
        if ($rootScope.restoreCaret) {
          $rootScope.restoreCaret();
        }
      }

      // ===== INDENT DRAG HANDLING =====

      /**
       * Captures the current indent state for an array of paragraphs
       * Used for undo/redo support
       * @param {Array} paragraphs - Array of paragraph elements
       * @returns {Object} - Map of pid to indent state
       */
      /**
       * Gets a unique key for a paragraph element (supports both pid-based and nested paragraphs)
       * @param {HTMLElement} paragraph - The paragraph element
       * @returns {string|null} - Unique key or null if not identifiable
       */
      function getParagraphKey(paragraph) {
        const pid = paragraph.getAttribute('pid');
        if (pid) {
          return pid;
        }
        // For nested paragraphs, use parent pid + nested index as composite key
        const nestedIndex = paragraph.getAttribute('data-nested-paragraph-index');
        if (nestedIndex) {
          const parentParagraph = paragraph.closest('[pid]');
          if (parentParagraph) {
            return parentParagraph.getAttribute('pid') + ':nested:' + nestedIndex;
          }
        }
        return null;
      }

      /**
       * Finds a paragraph element by its key (supports both pid-based and composite nested keys)
       * @param {string} key - The paragraph key
       * @returns {HTMLElement|null} - The paragraph element or null
       */
      function findParagraphByKey(key) {
        if (key.indexOf(':nested:') !== -1) {
          const parts = key.split(':nested:');
          const parentPid = parts[0];
          const nestedIndex = parts[1];
          const parent = document.querySelector('[pid="' + parentPid + '"]');
          if (parent) {
            return parent.querySelector('[data-nested-paragraph-index="' + nestedIndex + '"]');
          }
          return null;
        }
        return document.querySelector('[pid="' + key + '"]');
      }

      function captureIndentState(paragraphs) {
        const state = {};
        paragraphs.forEach(function(paragraph) {
          const key = getParagraphKey(paragraph);
          if (key) {
            const paragraphStyle = window.getComputedStyle(paragraph);
            state[key] = {
              textIndent: paragraphStyle.textIndent,
              marginLeft: paragraphStyle.marginLeft,
              marginRight: paragraphStyle.marginRight
            };
          }
        });
        return state;
      }

      /**
       * Restores indent state to paragraphs from a captured state object
       * @param {Object} state - Map of pid to indent state
       */
      function restoreIndentState(state) {
        const STEP_SIZE = window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 36;

        Object.keys(state).forEach(function(key) {
          const paragraph = findParagraphByKey(key);
          if (paragraph) {
            paragraph.style.textIndent = state[key].textIndent;
            paragraph.style.marginLeft = state[key].marginLeft;
            if (state[key].marginRight !== undefined) {
              paragraph.style.marginRight = state[key].marginRight;
            }

            // Set data attributes for indent system integration
            const textIndentPoints = parseInt(state[key].textIndent) || 0;
            const marginLeftPoints = parseInt(state[key].marginLeft) || 0;
            const marginRightPoints = parseInt(state[key].marginRight) || 0;

            if (textIndentPoints % STEP_SIZE === 0) {
              paragraph.setAttribute('data-text-indent', textIndentPoints / STEP_SIZE);
            }

            if (marginLeftPoints % STEP_SIZE === 0) {
              paragraph.setAttribute('data-paragraph-indent', marginLeftPoints / STEP_SIZE);
            }

            if (marginRightPoints % STEP_SIZE === 0 && marginRightPoints > 0) {
              paragraph.setAttribute('data-right-indent', marginRightPoints / STEP_SIZE);
            } else if (marginRightPoints === 0) {
              paragraph.removeAttribute('data-right-indent');
            }
          }
        });
      }

      /**
       * Handles mousedown on an indent marker to start dragging
       * @param {Event} event - The mousedown event
       * @param {string} indentType - 'first-line' or 'hanging'
       */
      vm.handleIndentMouseDown = function(event, indentType) {
        // Only handle left mouse button
        if (event.button !== 0) {
          return;
        }

        // Don't start if no active paragraph
        if (!vm.activeParagraph) {
          handleError('No active paragraph to adjust indent', 'handleIndentMouseDown', null);
          return;
        }

        // Preserve caret/selection before focus moves to the ruler
        if ($rootScope.saveSelection) {
          $rootScope.saveSelection(false);
        }

        // Prevent text selection during drag
        event.preventDefault();
        event.stopPropagation();

        // Get the scale element for bounds calculation
        const scaleElement = event.currentTarget.closest('.lp-ruler__scale');
        if (!scaleElement) {
          return;
        }

        // Get selected paragraphs for multi-paragraph support (capture at drag start)
        // When inside a nested paragraph, target the nested paragraph for indent changes
        const selectedParagraphs = getSelectedParagraphs();
        if (selectedParagraphs.length > 0) {
          vm.dragIndentTargetParagraphs = selectedParagraphs;
        } else if (vm.activeNestedParagraph) {
          vm.dragIndentTargetParagraphs = [vm.activeNestedParagraph];
        } else {
          vm.dragIndentTargetParagraphs = [vm.activeParagraph];
        }

        // Store parent base offset at drag start so we can subtract it when applying
        vm.dragIndentParentBaseLeft = vm.parentBaseMarginLeft;
        vm.dragIndentParentBaseRight = vm.parentBaseMarginRight;

        // Capture state at drag start for undo support
        vm.dragIndentBeforeState = captureIndentState(vm.dragIndentTargetParagraphs);

        // Get current position based on indent type
        const currentPosition = indentType === 'first-line'
          ? vm.firstLineIndentPosition
          : indentType === 'right-indent'
          ? vm.rightIndentPosition
          : vm.hangingIndentPosition;
        const currentPoints = indentType === 'first-line'
          ? vm.firstLineIndentPoints
          : indentType === 'right-indent'
          ? vm.rightIndentPoints
          : vm.hangingIndentPoints;

        // Capture the fixed indent position (the one that should NOT move)
        // Note: firstLineIndentPoints is textIndent (relative), hangingIndentPoints is marginLeft (absolute)
        // Absolute first-line position = hangingIndentPoints + firstLineIndentPoints
        if (indentType === 'first-line') {
          // When dragging first-line, hanging (marginLeft) stays fixed
          vm.dragIndentFixedPosition = vm.hangingIndentPoints;
          vm.dragIndentFixedVisualPosition = 0; // Not used when dragging first-line
          vm.dragIndentMoveBothTogether = false;
        } else if (indentType === 'hanging') {
          // When dragging hanging main piece, only change margin-left
          // First-line absolute position stays fixed
          const firstLineAbsolutePosition = vm.hangingIndentPoints + vm.firstLineIndentPoints;
          vm.dragIndentFixedPosition = firstLineAbsolutePosition; // Store first-line absolute position to keep fixed
          vm.dragIndentFixedVisualPosition = vm.firstLineIndentPosition; // Keep first-line visually fixed
          vm.dragIndentMoveBothTogether = false;
        } else if (indentType === 'hanging-ticker') {
          // When dragging hanging ticker, move both indicators together
          // Keep textIndent constant, so first-line absolute position = new hanging + original textIndent
          // Capture the original textIndent value (relative offset)
          vm.dragIndentFixedPosition = vm.firstLineIndentPoints; // Store textIndent (relative) to keep constant
          vm.dragIndentFixedVisualPosition = 0; // Not used - first-line will move
          vm.dragIndentMoveBothTogether = true; // Move both together when dragging ticker
        } else if (indentType === 'right-indent') {
          // Right indent is independent - no fixed position needed
          vm.dragIndentFixedPosition = 0; // Not used for right indent
          vm.dragIndentFixedVisualPosition = 0; // Not used for right indent
          vm.dragIndentMoveBothTogether = false;
        }

        // Initialize indent drag state
        vm.isDraggingIndent = true;
        vm.dragIndentType = indentType;
        vm.dragIndentPosition = currentPosition;
        vm.dragIndentPoints = currentPoints;
        vm.dragIndentStartPosition = currentPoints;
        vm.dragIndentScaleElement = scaleElement;

        // Add document-level listeners for drag
        document.addEventListener('mousemove', handleIndentDragMove);
        document.addEventListener('mouseup', handleIndentDragEnd);

        // Add body class to prevent text selection
        document.body.classList.add('lp-ruler-dragging');

        // Apply changes to update UI
        $scope.safeApply();
      };

      /**
       * Calculates right indent position from mouse event
       * @param {number} newPixelPosition - Mouse position in pixels from left edge of scale
       * @returns {Object} - Object with clampedPointPosition and displayPositionPoints
       */
      function getMaxRightIndentPoints() {
        // Keep enough content width so both hanging indent position and first-line
        // absolute position stay within ruler content bounds.
        const firstLineAbsolute = vm.hangingIndentPoints + vm.firstLineIndentPoints;
        const furthestLeftAnchor = Math.max(0, vm.hangingIndentPoints, firstLineAbsolute);
        return Math.max(0, vm.contentWidthPoints - furthestLeftAnchor);
      }

      function getMaxRightIndentPointsForParagraph(paragraph) {
        if (!paragraph) {
          return getMaxRightIndentPoints();
        }

        const paragraphStyle = window.getComputedStyle(paragraph);
        const marginLeftPoints = cssLengthToPoints(paragraphStyle.marginLeft);
        const textIndentPoints = cssLengthToPoints(paragraphStyle.textIndent);
        const normalizedMarginLeft = Math.max(0, marginLeftPoints);
        const normalizedFirstLineAbsolute = Math.max(0, normalizedMarginLeft + textIndentPoints);
        const furthestLeftAnchor = Math.max(0, normalizedMarginLeft, normalizedFirstLineAbsolute);

        // For nested paragraphs, account for parent's margins reducing available width
        let effectiveContentWidth = vm.contentWidthPoints;
        if (paragraph.hasAttribute('data-nested-paragraph-index')) {
          const parentParagraph = paragraph.closest('[pid]');
          if (parentParagraph) {
            const parentIndent = getParentIndentation(parentParagraph);
            effectiveContentWidth -= (parentIndent.marginLeftPoints + parentIndent.marginRightPoints);
          }
        }

        return Math.max(0, effectiveContentWidth - furthestLeftAnchor);
      }

      function getMaxRightIndentPointsForParagraphs(paragraphs) {
        const targets = Array.isArray(paragraphs) && paragraphs.length ? paragraphs : [vm.activeParagraph];
        const paragraphMaxValues = targets
          .filter(function(paragraph) { return !!paragraph; })
          .map(function(paragraph) { return getMaxRightIndentPointsForParagraph(paragraph); });

        if (!paragraphMaxValues.length) {
          return getMaxRightIndentPoints();
        }

        return Math.min.apply(null, paragraphMaxValues);
      }

      function normalizeParagraphLeftAnchors(paragraph, updateDataAttributes) {
        if (!paragraph) {
          return;
        }

        const STEP_SIZE = window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 36;
        const paragraphStyle = window.getComputedStyle(paragraph);
        const marginLeftPoints = cssLengthToPoints(paragraphStyle.marginLeft);
        const textIndentPoints = cssLengthToPoints(paragraphStyle.textIndent);

        let clampedMarginLeftPoints = Math.max(0, marginLeftPoints);
        let clampedTextIndentPoints = textIndentPoints;
        const firstLineAbsolute = clampedMarginLeftPoints + clampedTextIndentPoints;

        // Keep first-line absolute anchor in content bounds.
        if (firstLineAbsolute < 0) {
          clampedTextIndentPoints = -clampedMarginLeftPoints;
        }

        paragraph.style.marginLeft = clampedMarginLeftPoints + 'pt';
        paragraph.style.textIndent = clampedTextIndentPoints + 'pt';

        if (updateDataAttributes) {
          const marginLeftSteps = clampedMarginLeftPoints / STEP_SIZE;
          const textIndentSteps = clampedTextIndentPoints / STEP_SIZE;

          if (clampedMarginLeftPoints === 0) {
            paragraph.removeAttribute('data-paragraph-indent');
          } else {
            paragraph.setAttribute('data-paragraph-indent', marginLeftSteps);
          }

          if (clampedTextIndentPoints === 0) {
            paragraph.removeAttribute('data-text-indent');
          } else {
            paragraph.setAttribute('data-text-indent', textIndentSteps);
          }
        }
      }

      function calculateRightIndentPosition(newPixelPosition) {
        // For right indent: calculate margin-right from indicator position
        // Indicator position from right edge of ruler = scaleWidth - newPixelPosition
        // Right margin position from right edge = rightMarginPosition (where margin-right = 0)
        // marginRight (in pixels) = (scaleWidth - newPixelPosition) - rightMarginPosition
        
        // Calculate indicator position from right edge
        const indicatorPositionFromRight = vm.scaleWidth - newPixelPosition;
        
        // Calculate margin-right in pixels (distance indicator has moved from right margin)
        let marginRightPixels = indicatorPositionFromRight - vm.rightMarginPosition;
        
        // Ensure margin-right is non-negative (indicator can go back to right margin, but not beyond)
        marginRightPixels = Math.max(0, marginRightPixels);
        
        // Constraint: prevent going beyond tick 6 (similar to left margin constraint at tick 14)
        const maxMarginRightPixels = (vm.scaleWidth - vm.tick6PixelPosition) - vm.rightMarginPosition;
        marginRightPixels = Math.min(marginRightPixels, maxMarginRightPixels);
        
        // Convert to points
        let clampedPointPosition = TabRenderer.pixelsToPoints(marginRightPixels);
        
        // Ensure paragraph doesn't become impossibly narrow (minimum width check)
        const maxRightIndent = getMaxRightIndentPointsForParagraphs(vm.dragIndentTargetParagraphs);
        clampedPointPosition = Math.min(clampedPointPosition, maxRightIndent);
        
        // Snap to quarter step size
        clampedPointPosition = snapToQuarterStepSize(clampedPointPosition);
        // Re-clamp after snapping in case rounding moved us past max.
        clampedPointPosition = Math.max(0, Math.min(clampedPointPosition, maxRightIndent));
        
        // For display: position from right edge in pixels
        const rightIndentDisplayPixels = vm.rightMarginPosition + TabRenderer.pointsToPixels(clampedPointPosition);
        const displayPositionPoints = rightIndentDisplayPixels; // In pixels for right positioning
        
        return {
          clampedPointPosition: clampedPointPosition,
          displayPositionPoints: displayPositionPoints
        };
      }
      
      /**
       * Calculates left indent position from mouse event
       * @param {number} newPixelPosition - Mouse position in pixels from left edge of scale
       * @returns {Object} - Object with clampedPointPosition and displayPositionPoints
       */
      function calculateLeftIndentPosition(newPixelPosition) {
        // For left indents: position is from left edge of content
        // Convert from ruler pixel position to points offset (relative to content start)
        let contentAreaOffsetPoints = rulerPixelsToPoints(newPixelPosition);
        
        // CRITICAL: Enforce MAX_POSITION_POINTS as absolute maximum BEFORE snapping
        if (contentAreaOffsetPoints > MAX_POSITION_POINTS) {
          contentAreaOffsetPoints = MAX_POSITION_POINTS;
        }
        
        // Snap to quarter step size and clamp to valid range (0 to MAX_POSITION_POINTS)
        let newPointPosition = snapToQuarterStepSize(contentAreaOffsetPoints);
        if (newPointPosition > MAX_POSITION_POINTS) {
          newPointPosition = MAX_POSITION_POINTS;
        }
        const clampedPointPosition = Math.max(0, Math.min(newPointPosition, MAX_POSITION_POINTS));
        
        // Update drag position for visual feedback (convert back to points)
        let displayPositionPoints = pointsToRulerPoints(clampedPointPosition);
        
        // Ensure position is within valid bounds
        const maxDisplayPoints = TabRenderer.pixelsToPoints(vm.tick14PixelPosition);
        displayPositionPoints = Math.max(vm.paddingLeftPoints, Math.min(displayPositionPoints, maxDisplayPoints));
        
        // Double-check: Ensure display position never exceeds tick 14
        if (displayPositionPoints > maxDisplayPoints) {
          displayPositionPoints = maxDisplayPoints;
        }
        
        return {
          clampedPointPosition: clampedPointPosition,
          displayPositionPoints: displayPositionPoints
        };
      }

      function clampLeftDisplayPosition(displayPositionPoints) {
        const maxDisplayPoints = TabRenderer.pixelsToPoints(vm.tick14PixelPosition);
        return Math.max(vm.paddingLeftPoints, Math.min(displayPositionPoints, maxDisplayPoints));
      }

      function getMinHangingTickerPositionPoints() {
        // While moving both together, keep first-line absolute position in content bounds:
        // firstLineAbsolute = hanging + textIndent >= 0  =>  hanging >= -textIndent.
        if (!vm.dragIndentMoveBothTogether || vm.dragIndentType !== 'hanging-ticker') {
          return 0;
        }

        const textIndentPoints = vm.dragIndentFixedPosition || 0;
        return Math.max(0, -textIndentPoints);
      }
      
      /**
       * Updates visual feedback properties for indent indicators during drag
       * @param {number} clampedPointPosition - The clamped position in points
       * @param {number} displayPositionPoints - The display position in points/pixels
       */
      function updateIndentVisualFeedback(clampedPointPosition, displayPositionPoints) {
        if (vm.dragIndentType === 'first-line') {
          // When dragging first-line, hanging stays fixed
          vm.firstLineIndentPoints = clampedPointPosition - vm.dragIndentFixedPosition; // textIndent (relative)
          vm.firstLineIndentPosition = displayPositionPoints;
          vm.hasFirstLineIndent = clampedPointPosition !== vm.dragIndentFixedPosition;
        } else if (vm.dragIndentType === 'hanging' || vm.dragIndentType === 'hanging-ticker') {
          vm.hangingIndentPoints = clampedPointPosition; // marginLeft (absolute)
          vm.hangingIndentPosition = displayPositionPoints;
          vm.hasHangingIndent = clampedPointPosition > 0;
          
          if (vm.dragIndentMoveBothTogether) {
            // Dragging ticker: move both indicators together
            const originalTextIndent = vm.dragIndentFixedPosition;
            
            const newFirstLineAbsolutePosition = clampedPointPosition + originalTextIndent;
            const newFirstLineDisplayPoints = pointsToRulerPoints(newFirstLineAbsolutePosition);
            const maxDisplayPoints = TabRenderer.pixelsToPoints(vm.tick14PixelPosition);
            const constrainedFirstLineDisplayPoints = Math.max(vm.paddingLeftPoints, Math.min(newFirstLineDisplayPoints, maxDisplayPoints));
            
            // If the first-line position was constrained to the forbidden zone, adjust textIndent accordingly
            if (constrainedFirstLineDisplayPoints === vm.paddingLeftPoints && newFirstLineDisplayPoints < vm.paddingLeftPoints) {
              // First-line is constrained to forbidden zone boundary
              // Calculate the adjusted textIndent so that first-line absolute position = 0 (forbidden zone boundary in content area)
              const adjustedTextIndent = -clampedPointPosition;
              vm.firstLineIndentPoints = adjustedTextIndent;
            } else {
              // No constraint applied, keep original textIndent
              vm.firstLineIndentPoints = originalTextIndent;
            }
            
            vm.firstLineIndentPosition = constrainedFirstLineDisplayPoints;
            vm.hasFirstLineIndent = vm.firstLineIndentPoints !== 0;
          } else {
            // Dragging hanging main piece: only change margin-left, first-line absolute stays fixed
            const firstLineAbsolutePosition = vm.dragIndentFixedPosition;
            vm.firstLineIndentPoints = firstLineAbsolutePosition - clampedPointPosition;
            vm.firstLineIndentPosition = vm.dragIndentFixedVisualPosition;
            vm.hasFirstLineIndent = firstLineAbsolutePosition !== clampedPointPosition;
          }
        } else if (vm.dragIndentType === 'right-indent') {
          vm.rightIndentPoints = clampedPointPosition;
          vm.rightIndentPosition = displayPositionPoints;
          vm.hasRightIndent = clampedPointPosition > 0;
        }
      }
      
      /**
       * Applies right indent to a paragraph
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {number} positionPoints - Margin-right value in points
       * @param {boolean} updateDataAttributes - Whether to update data attributes
       */
      function applyRightIndent(paragraph, positionPoints, updateDataAttributes) {
        const STEP_SIZE = window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 36;
        if (updateDataAttributes) {
          normalizeParagraphLeftAnchors(paragraph, true);
        }
        const maxRightIndentPoints = getMaxRightIndentPointsForParagraph(paragraph);
        const clampedPositionPoints = Math.max(0, Math.min(positionPoints, maxRightIndentPoints));
        const marginRightValue = clampedPositionPoints + 'pt';
        const marginRightSteps = clampedPositionPoints / STEP_SIZE;
        
        // Set marginRight (right indent)
        if (clampedPositionPoints === 0) {
          paragraph.style.marginRight = '0pt';
          if (updateDataAttributes) {
            paragraph.removeAttribute('data-right-indent');
          }
        } else {
          paragraph.style.marginRight = marginRightValue;
          if (updateDataAttributes) {
            paragraph.setAttribute('data-right-indent', marginRightSteps);
          }
        }
      }
      
      /**
       * Applies first-line indent to a paragraph
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {number} positionPoints - Absolute first-line position in points
       * @param {number} fixedPositionPoints - Fixed hanging position (margin-left)
       * @param {boolean} updateDataAttributes - Whether to update data attributes
       */
      function applyFirstLineIndent(paragraph, positionPoints, fixedPositionPoints, updateDataAttributes) {
        const STEP_SIZE = window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 36;
        const hangingPosition = fixedPositionPoints || 0;
        const marginLeftValue = hangingPosition + 'pt';
        const textIndentValue = (positionPoints - hangingPosition) + 'pt';
        const textIndentSteps = (positionPoints - hangingPosition) / STEP_SIZE;
        const marginLeftSteps = hangingPosition / STEP_SIZE;

        // Set marginLeft (hanging indent absolute position)
        if (hangingPosition === 0) {
          paragraph.style.marginLeft = '0pt';
          if (updateDataAttributes) {
            paragraph.removeAttribute('data-paragraph-indent');
          }
        } else {
          paragraph.style.marginLeft = marginLeftValue;
          if (updateDataAttributes) {
            paragraph.setAttribute('data-paragraph-indent', marginLeftSteps);
          }
        }

        // Set textIndent (first-line relative to hanging)
        if (positionPoints === hangingPosition) {
          paragraph.style.textIndent = '0pt';
          if (updateDataAttributes) {
            paragraph.removeAttribute('data-text-indent');
          }
        } else {
          paragraph.style.textIndent = textIndentValue;
          if (updateDataAttributes) {
            paragraph.setAttribute('data-text-indent', textIndentSteps);
          }
        }
      }
      
      /**
       * Applies hanging indent to a paragraph
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {number} positionPoints - Absolute hanging position (margin-left) in points
       * @param {number} fixedPositionPoints - Fixed first-line absolute position or textIndent value
       * @param {boolean} moveBothTogether - When true, move both indicators together
       * @param {boolean} updateDataAttributes - Whether to update data attributes
       */
      function applyHangingIndent(paragraph, positionPoints, fixedPositionPoints, moveBothTogether, updateDataAttributes) {
        const STEP_SIZE = window.CONSTS && window.CONSTS.STEP_SIZE ? window.CONSTS.STEP_SIZE : 36;
        
        if (moveBothTogether) {
          // Dragging ticker: move both indicators together
          // Keep textIndent constant (stored in fixedPositionPoints), update marginLeft
          const textIndentValue = fixedPositionPoints; // Original textIndent (relative) stays constant
          const marginLeftValue = positionPoints + 'pt';
          const marginLeftSteps = positionPoints / STEP_SIZE;
          const textIndentSteps = textIndentValue / STEP_SIZE;

          // Set marginLeft (hanging indent absolute position)
          if (positionPoints === 0) {
            paragraph.style.marginLeft = '0pt';
            if (updateDataAttributes) {
              paragraph.removeAttribute('data-paragraph-indent');
            }
          } else {
            paragraph.style.marginLeft = marginLeftValue;
            if (updateDataAttributes) {
              paragraph.setAttribute('data-paragraph-indent', marginLeftSteps);
            }
          }

          // Set textIndent (first-line relative to hanging) - stays constant
          if (textIndentValue === 0) {
            paragraph.style.textIndent = '0pt';
            if (updateDataAttributes) {
              paragraph.removeAttribute('data-text-indent');
            }
          } else {
            paragraph.style.textIndent = textIndentValue + 'pt';
            if (updateDataAttributes) {
              paragraph.setAttribute('data-text-indent', textIndentSteps);
            }
          }
        } else {
          // Dragging hanging main piece: only change margin-left, first-line absolute stays fixed
          const firstLineAbsolutePosition = fixedPositionPoints || 0;
          const marginLeftValue = positionPoints + 'pt';
          const textIndentValue = (firstLineAbsolutePosition - positionPoints) + 'pt';
          const textIndentSteps = (firstLineAbsolutePosition - positionPoints) / STEP_SIZE;
          const marginLeftSteps = positionPoints / STEP_SIZE;

          // Set marginLeft (hanging indent absolute position)
          if (positionPoints === 0) {
            paragraph.style.marginLeft = '0pt';
            if (updateDataAttributes) {
              paragraph.removeAttribute('data-paragraph-indent');
            }
          } else {
            paragraph.style.marginLeft = marginLeftValue;
            if (updateDataAttributes) {
              paragraph.setAttribute('data-paragraph-indent', marginLeftSteps);
            }
          }

          // Set textIndent (first-line relative to hanging)
          if (firstLineAbsolutePosition === positionPoints) {
            paragraph.style.textIndent = '0pt';
            if (updateDataAttributes) {
              paragraph.removeAttribute('data-text-indent');
            }
          } else {
            paragraph.style.textIndent = textIndentValue;
            if (updateDataAttributes) {
              paragraph.setAttribute('data-text-indent', textIndentSteps);
            }
          }
        }
      }

      /**
       * Finds the lease editor scope that owns `lease.restyle`.
       * The ruler uses an isolated scope, so we need to reach the editor scope explicitly.
       * @returns {Object|null} - Angular scope containing lease + setOriginalStyle, or null
       */
      function getLeaseEditorScope() {
        const leaseRoot = document.querySelector('.lease.lease--original');
        if (!leaseRoot || typeof angular === 'undefined') {
          return null;
        }

        let scope = angular.element(leaseRoot).scope() || angular.element(leaseRoot).isolateScope();
        while (scope) {
          if (scope.lease && typeof scope.setOriginalStyle === 'function') {
            return scope;
          }
          scope = scope.$parent;
        }

        return null;
      }

      function cssLengthToPoints(lengthValue) {
        if (lengthValue === null || lengthValue === undefined || lengthValue === '') {
          return 0;
        }

        const normalizedLength = String(lengthValue).trim().toLowerCase();
        const numericValue = parseFloat(normalizedLength);

        if (isNaN(numericValue)) {
          return 0;
        }

        if (normalizedLength.endsWith('px')) {
          return numericValue * (72 / 96);
        }

        return numericValue;
      }

      /**
       * Mirrors paragraph indent styles into lease.restyle so persisted saves include
       * changes coming from ruler interactions.
       * @param {Array} paragraphs - Paragraph elements that were updated
       */
      function syncIndentRestyleForParagraphs(paragraphs) {
        const editorScope = getLeaseEditorScope();
        if (!editorScope || !editorScope.lease || !Array.isArray(paragraphs)) {
          return;
        }

        if (!editorScope.lease.restyle) {
          editorScope.lease.restyle = [];
        }

        const stepSize = getStepSize();
        const defaultFontSize = editorScope.defaultFont && editorScope.defaultFont.fontSize
          ? editorScope.defaultFont.fontSize
          : '10pt';
        const defaultLineHeight = editorScope.defaultFont && editorScope.defaultFont.lineHeight
          ? editorScope.defaultFont.lineHeight
          : 'normal';

        paragraphs.forEach(function(paragraph) {
          if (!paragraph) {
            return;
          }

          const pid = paragraph.getAttribute('pid');
          if (!pid) {
            // For nested paragraphs (no pid), their styles are persisted as part of the
            // parent's content save. Mark the document as dirty so content changes get saved.
            if (paragraph.hasAttribute('data-nested-paragraph-index') && editorScope.markDirty) {
              editorScope.markDirty();
            }
            return;
          }

          if (!editorScope.lease.restyle[pid]) {
            const originalMarginLeft = paragraph.style.marginLeft || '0pt';
            const originalTextIndent = paragraph.style.textIndent || '0pt';
            const originalMarginRight = paragraph.style.marginRight || '0pt';
            const originalTextAlign = paragraph.style.textAlign || 'start';
            const originalFontSize = paragraph.style.fontSize || defaultFontSize;
            const originalLineHeight = paragraph.style.lineHeight || defaultLineHeight;
            const originalParagraphIndent = cssLengthToPoints(originalMarginLeft) / stepSize;
            const originalTextIndentSteps = cssLengthToPoints(originalTextIndent) / stepSize;
            const originalRightIndent = cssLengthToPoints(originalMarginRight) / stepSize;

            editorScope.lease.restyle[pid] = {
              paragraphId: pid,
              originalParagraphIndent: originalMarginLeft,
              originalTextIndent: originalTextIndent,
              originalRightIndent: originalMarginRight,
              originalTextAlign: originalTextAlign,
              originalFontSize: originalFontSize,
              originalLineHeight: originalLineHeight,
              paragraphIndent: isNaN(originalParagraphIndent) ? 0 : originalParagraphIndent,
              textIndent: isNaN(originalTextIndentSteps) ? 0 : originalTextIndentSteps,
              rightIndent: isNaN(originalRightIndent) ? 0 : originalRightIndent,
              textAlign: originalTextAlign,
              fontSize: originalFontSize,
              lineHeight: originalLineHeight
            };
          }

          const textIndentPoints = cssLengthToPoints(paragraph.style.textIndent);
          const marginLeftPoints = cssLengthToPoints(paragraph.style.marginLeft);
          const marginRightPoints = cssLengthToPoints(paragraph.style.marginRight);
          editorScope.lease.restyle[pid].textIndent = textIndentPoints / stepSize;
          editorScope.lease.restyle[pid].paragraphIndent = marginLeftPoints / stepSize;
          editorScope.lease.restyle[pid].rightIndent = marginRightPoints / stepSize;

          editorScope.setOriginalStyle(paragraph, editorScope.lease.restyle[pid]);
        });
      }
      
      /**
       * Applies indent changes to target paragraphs
       * @param {number} positionPoints - Position in points (absolute position on ruler for left indents, margin-right value for right indent)
       * @param {Array} paragraphs - Array of paragraph elements to update
       * @param {string} indentType - 'first-line', 'hanging', 'hanging-ticker', or 'right-indent'
       * @param {boolean} updateDataAttributes - Whether to update data attributes (true) or just styles (false)
       * @param {number} fixedPositionPoints - Fixed indent absolute position (first-line absolute when dragging hanging, hanging absolute when dragging first-line)
       * @param {boolean} moveBothTogether - When true and dragging hanging, move both indicators together (both at 0 case)
       */
      function applyIndentToParagraphs(positionPoints, paragraphs, indentType, updateDataAttributes, fixedPositionPoints, moveBothTogether) {
        paragraphs.forEach(function(paragraph) {
          if (indentType === 'right-indent') {
            applyRightIndent(paragraph, positionPoints, updateDataAttributes);
          } else if (indentType === 'first-line') {
            applyFirstLineIndent(paragraph, positionPoints, fixedPositionPoints, updateDataAttributes);
          } else {
            // 'hanging' or 'hanging-ticker'
            applyHangingIndent(paragraph, positionPoints, fixedPositionPoints, moveBothTogether, updateDataAttributes);
          }
        });

        if (updateDataAttributes) {
          syncIndentRestyleForParagraphs(paragraphs);
        }
      }

      /**
       * Handles mouse movement during indent drag
       * @param {Event} event - The mousemove event
       */
      function handleIndentDragMove(event) {
        if (!vm.isDraggingIndent || !vm.dragIndentScaleElement) {
          return;
        }

        // Calculate new position
        const scaleRect = vm.dragIndentScaleElement.getBoundingClientRect();
        let newPixelPosition = event.clientX - scaleRect.left;

        // Constrain based on indent type
        let minPosition, maxPosition;
        if (vm.dragIndentType === 'right-indent') {
          minPosition = vm.tick6PixelPosition;
          maxPosition = vm.scaleWidth;
        } else {
          minPosition = vm.paddingLeft;
          maxPosition = vm.tick14PixelPosition;
        }
        
        // If mouse is significantly outside the ruler's horizontal bounds, don't update position
        if (newPixelPosition < minPosition - INDENT_DRAG_TOLERANCE || newPixelPosition > maxPosition + INDENT_DRAG_TOLERANCE) {
          return;
        }
        
        // Constrain to valid area
        newPixelPosition = Math.max(minPosition, Math.min(newPixelPosition, maxPosition));

        // Calculate position based on indent type
        let positionResult;
        if (vm.dragIndentType === 'right-indent') {
          positionResult = calculateRightIndentPosition(newPixelPosition);
        } else {
          positionResult = calculateLeftIndentPosition(newPixelPosition);
        }
        
        let clampedPointPosition = positionResult.clampedPointPosition;
        let displayPositionPoints = positionResult.displayPositionPoints;

        // Extra guard for hanging ticker: do not allow first-line marker past left bounds.
        const minTickerPosition = getMinHangingTickerPositionPoints();
        if (clampedPointPosition < minTickerPosition) {
          clampedPointPosition = minTickerPosition;
          displayPositionPoints = clampLeftDisplayPosition(pointsToRulerPoints(clampedPointPosition));
        }

        // Only update if position actually changed (avoid unnecessary DOM updates)
        if (Math.abs(clampedPointPosition - vm.dragIndentPoints) < 0.1) {
          return;
        }
        
        vm.dragIndentPosition = displayPositionPoints;
        vm.dragIndentPoints = clampedPointPosition;

        // Calculate adjusted textIndent if dragging hanging ticker and first-line would be constrained
        let adjustedFixedPosition = vm.dragIndentFixedPosition;
        if (vm.dragIndentMoveBothTogether && vm.dragIndentType === 'hanging-ticker') {
          const originalTextIndent = vm.dragIndentFixedPosition;
          const newFirstLineAbsolutePosition = clampedPointPosition + originalTextIndent;
          const newFirstLineDisplayPoints = pointsToRulerPoints(newFirstLineAbsolutePosition);
          // If first-line would be constrained to forbidden zone, adjust textIndent
          if (newFirstLineDisplayPoints < vm.paddingLeftPoints) {
            adjustedFixedPosition = -clampedPointPosition;
          }
        }

        // Apply indent changes immediately during drag (without updating data attributes yet)
        // When targeting nested paragraphs, subtract parent base offset so we apply nested-only values
        const targetParagraphs = vm.dragIndentTargetParagraphs || [vm.activeParagraph];
        if (targetParagraphs.length > 0 && vm.dragIndentType) {
          let applyPosition = clampedPointPosition;
          let applyFixedPosition = adjustedFixedPosition;
          if (vm.dragIndentParentBaseLeft > 0 || vm.dragIndentParentBaseRight > 0) {
            if (vm.dragIndentType === 'right-indent') {
              applyPosition = Math.max(0, clampedPointPosition - vm.dragIndentParentBaseRight);
            } else {
              applyPosition = Math.max(0, clampedPointPosition - vm.dragIndentParentBaseLeft);
              // Adjust fixed position for hanging drag (first-line absolute is also effective)
              if (vm.dragIndentType === 'hanging' && !vm.dragIndentMoveBothTogether) {
                applyFixedPosition = Math.max(0, adjustedFixedPosition - vm.dragIndentParentBaseLeft);
              } else if (vm.dragIndentType === 'first-line') {
                applyFixedPosition = Math.max(0, adjustedFixedPosition - vm.dragIndentParentBaseLeft);
              }
            }
          }
          applyIndentToParagraphs(applyPosition, targetParagraphs, vm.dragIndentType, false, applyFixedPosition, vm.dragIndentMoveBothTogether);
        }

        // Update indent positions for visual feedback
        updateIndentVisualFeedback(clampedPointPosition, displayPositionPoints);

        // Apply changes to update UI (debounced for performance)
        debouncedSafeApply();
      }

      /**
       * Applies final indent changes with data attributes and updates visual feedback
       * @param {number} finalPosition - Final position in points
       * @param {Array} targetParagraphs - Target paragraphs to update
       */
      function applyFinalIndentChanges(finalPosition, targetParagraphs) {
        // Calculate adjusted textIndent if dragging hanging ticker and first-line would be constrained
        let adjustedFixedPosition = vm.dragIndentFixedPosition;
        if (vm.dragIndentMoveBothTogether && vm.dragIndentType === 'hanging-ticker') {
          const originalTextIndent = vm.dragIndentFixedPosition;
          const newFirstLineAbsolutePosition = finalPosition + originalTextIndent;
          const newFirstLineDisplayPoints = pointsToRulerPoints(newFirstLineAbsolutePosition);
          // If first-line would be constrained to forbidden zone, adjust textIndent
          if (newFirstLineDisplayPoints < vm.paddingLeftPoints) {
            adjustedFixedPosition = -finalPosition;
          }
        }

        // Apply indent changes with data attributes (for persistence)
        // When targeting nested paragraphs, subtract parent base offset so we apply nested-only values
        let applyPosition = finalPosition;
        let applyFixedPosition = adjustedFixedPosition;
        if (vm.dragIndentParentBaseLeft > 0 || vm.dragIndentParentBaseRight > 0) {
          if (vm.dragIndentType === 'right-indent') {
            applyPosition = Math.max(0, finalPosition - vm.dragIndentParentBaseRight);
          } else {
            applyPosition = Math.max(0, finalPosition - vm.dragIndentParentBaseLeft);
            if (vm.dragIndentType === 'hanging' && !vm.dragIndentMoveBothTogether) {
              applyFixedPosition = Math.max(0, adjustedFixedPosition - vm.dragIndentParentBaseLeft);
            } else if (vm.dragIndentType === 'first-line') {
              applyFixedPosition = Math.max(0, adjustedFixedPosition - vm.dragIndentParentBaseLeft);
            }
          }
        }
        applyIndentToParagraphs(applyPosition, targetParagraphs, vm.dragIndentType, true, applyFixedPosition, vm.dragIndentMoveBothTogether);

        // Update indent positions to reflect the final change
        if (vm.dragIndentType === 'right-indent') {
          vm.rightIndentPoints = finalPosition;
          const rightIndentDisplayPixels = vm.rightMarginPosition + TabRenderer.pointsToPixels(finalPosition);
          vm.rightIndentPosition = rightIndentDisplayPixels;
          vm.hasRightIndent = finalPosition > 0;
        } else {
          const finalDisplayPoints = pointsToRulerPoints(finalPosition);
          const constrainedDisplayPoints = Math.max(vm.paddingLeftPoints, finalDisplayPoints);
          
          if (vm.dragIndentType === 'first-line') {
            vm.firstLineIndentPoints = finalPosition - vm.dragIndentFixedPosition;
            vm.firstLineIndentPosition = constrainedDisplayPoints;
            vm.hasFirstLineIndent = finalPosition !== vm.dragIndentFixedPosition;
          } else if (vm.dragIndentType === 'hanging' || vm.dragIndentType === 'hanging-ticker') {
            vm.hangingIndentPoints = finalPosition;
            vm.hangingIndentPosition = constrainedDisplayPoints;
            vm.hasHangingIndent = finalPosition > 0;
            
            if (vm.dragIndentMoveBothTogether) {
              const originalTextIndent = vm.dragIndentFixedPosition;
              
              const newFirstLineAbsolutePosition = finalPosition + originalTextIndent;
              const newFirstLineDisplayPoints = pointsToRulerPoints(newFirstLineAbsolutePosition);
              const maxDisplayPoints = TabRenderer.pixelsToPoints(vm.tick14PixelPosition);
              const constrainedFirstLineDisplayPoints = Math.max(vm.paddingLeftPoints, Math.min(newFirstLineDisplayPoints, maxDisplayPoints));
              
              // If the first-line position was constrained to the forbidden zone, adjust textIndent accordingly
              if (constrainedFirstLineDisplayPoints === vm.paddingLeftPoints && newFirstLineDisplayPoints < vm.paddingLeftPoints) {
                // First-line is constrained to forbidden zone boundary
                // Calculate the adjusted textIndent so that first-line absolute position = 0 (forbidden zone boundary in content area)
                const adjustedTextIndent = -finalPosition;
                vm.firstLineIndentPoints = adjustedTextIndent;
              } else {
                // No constraint applied, keep original textIndent
                vm.firstLineIndentPoints = originalTextIndent;
              }
              
              vm.firstLineIndentPosition = constrainedFirstLineDisplayPoints;
              vm.hasFirstLineIndent = vm.firstLineIndentPoints !== 0;
            } else {
              const firstLineAbsolutePosition = vm.dragIndentFixedPosition;
              vm.firstLineIndentPoints = firstLineAbsolutePosition - finalPosition;
              vm.firstLineIndentPosition = vm.dragIndentFixedVisualPosition;
              vm.hasFirstLineIndent = firstLineAbsolutePosition !== finalPosition;
            }
          }
        }
      }
      
      /**
       * Broadcasts indent change event and tracks analytics
       * @param {number} originalPosition - Original position in points
       * @param {number} finalPosition - Final position in points
       * @param {Array} targetParagraphs - Target paragraphs
       * @param {Object} beforeState - State before modification
       */
      function broadcastIndentChangeEvent(originalPosition, finalPosition, targetParagraphs, beforeState) {
        // Capture state after modification for redo support
        const afterState = captureIndentState(targetParagraphs);

        // Broadcast change event for undo/redo registration
        $rootScope.$broadcast('indent:changed', {
          paragraph: vm.activeParagraph,
          action: 'adjust-' + vm.dragIndentType,
          indentType: vm.dragIndentType,
          oldPosition: originalPosition,
          newPosition: finalPosition,
          paragraphCount: targetParagraphs.length,
          beforeState: beforeState,
          afterState: afterState,
          restoreState: restoreIndentState
        });

        // Track analytics
        if (window.track && window.track.event) {
          window.track.event(new IndentAdjustedEvent({
            indentType: vm.dragIndentType,
            oldPosition: originalPosition,
            newPosition: finalPosition,
            paragraphCount: targetParagraphs.length,
            context: $rootScope.getContext ? $rootScope.getContext() : {}
          }));
        }
      }
      
      /**
       * Resets all indent drag state variables
       */
      function resetIndentDragState() {
        vm.isDraggingIndent = false;
        vm.dragIndentType = null;
        vm.dragIndentPosition = 0;
        vm.dragIndentPoints = 0;
        vm.dragIndentStartPosition = 0;
        vm.dragIndentFixedPosition = 0;
        vm.dragIndentFixedVisualPosition = 0;
        vm.dragIndentMoveBothTogether = false;
        vm.dragIndentScaleElement = null;
        vm.dragIndentBeforeState = null;
        vm.dragIndentTargetParagraphs = null;
        vm.dragIndentParentBaseLeft = 0;
        vm.dragIndentParentBaseRight = 0;
      }
      
      /**
       * Handles mouse up to end indent dragging
       * @param {Event} event - The mouseup event
       */
      function handleIndentDragEnd(event) {
        if (!vm.isDraggingIndent) {
          return;
        }

        // Remove document-level listeners
        document.removeEventListener('mousemove', handleIndentDragMove);
        document.removeEventListener('mouseup', handleIndentDragEnd);

        // Remove body class
        document.body.classList.remove('lp-ruler-dragging');

        // Calculate final position (ensure it's snapped to quarter STEP_SIZE for finer control)
        let finalPosition = snapToQuarterStepSize(vm.dragIndentPoints);
        if (vm.dragIndentType === 'right-indent') {
          finalPosition = Math.min(finalPosition, getMaxRightIndentPointsForParagraphs(vm.dragIndentTargetParagraphs));
        } else if (vm.dragIndentType === 'hanging-ticker' && vm.dragIndentMoveBothTogether) {
          finalPosition = Math.max(finalPosition, getMinHangingTickerPositionPoints());
        }
        finalPosition = Math.max(0, finalPosition);
        const originalPosition = vm.dragIndentStartPosition;
        const movedSignificantly = Math.abs(finalPosition - originalPosition) > 1;
        const beforeState = vm.dragIndentBeforeState;
        const targetParagraphs = vm.dragIndentTargetParagraphs || [vm.activeParagraph];

        // Apply the change if moved significantly
        if (movedSignificantly && vm.activeParagraph) {
          applyFinalIndentChanges(finalPosition, targetParagraphs);
          broadcastIndentChangeEvent(originalPosition, finalPosition, targetParagraphs, beforeState);
        }

        // Reset indent drag state
        resetIndentDragState();

        // Apply changes
        $scope.safeApply();

        // Restore caret/selection back to the document after drag completes
        if ($rootScope.restoreCaret) {
          $rootScope.restoreCaret();
        }
      }

      /**
       * Updates a tabstop's position in a paragraph
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {number} oldPosition - The original position in points
       * @param {number} newPosition - The new position in points
       * @returns {boolean} - True if update was successful
       */
      function updateTabstopPosition(paragraph, oldPosition, newPosition) {
        // Get existing tabstops
        const tabstops = TabstopService.parseTabstopsFromElement(paragraph);
        
        // Find the tabstop at the old position (within tolerance)
        const tabstopIndex = tabstops.findIndex(function(ts) {
          return Math.abs(ts.position - oldPosition) <= TABSTOP_POSITION_TOLERANCE;
        });
        
        if (tabstopIndex === -1) {
          handleError('Could not find tabstop at position ' + oldPosition + ' in paragraph', 'updateTabstopPosition', null);
          return false;
        }
        
        // Check if new position conflicts with existing tabstop
        const conflictIndex = tabstops.findIndex(function(ts, idx) {
          return idx !== tabstopIndex && Math.abs(ts.position - newPosition) <= TABSTOP_POSITION_TOLERANCE;
        });
        
        if (conflictIndex !== -1) {
          handleError('Cannot move tabstop - another tabstop exists at position ' + newPosition, 'updateTabstopPosition', null);
          return false;
        }
        
        // Update the position
        tabstops[tabstopIndex].position = newPosition;
        
        // Set tabstops on paragraph (will be sorted automatically)
        return TabstopService.setTabstopsOnElement(paragraph, tabstops);
      }
      
      /**
       * Captures the current tabstop state for an array of paragraphs
       * Used for undo/redo support
       * @param {Array} paragraphs - Array of paragraph elements
       * @returns {Object} - Map of pid to tabstop array
       */
      function captureTabstopState(paragraphs) {
        const state = {};
        paragraphs.forEach(function(paragraph) {
          const sectionId = paragraph.getAttribute('pid');
          if (sectionId) {
            // Deep clone the tabstops to avoid reference issues
            const tabstops = TabstopService.parseTabstopsFromElement(paragraph);
            state[sectionId] = JSON.parse(JSON.stringify(tabstops));
          }
        });
        return state;
      }

      /**
       * Restores tabstop state to paragraphs from a captured state object
       * @param {Object} state - Map of pid to tabstop array
       */
      function restoreTabstopState(state) {
        Object.keys(state).forEach(function(sectionId) {
          const paragraph = document.querySelector('[pid="' + sectionId + '"]');
          if (paragraph) {
            TabstopService.setTabstopsOnElement(paragraph, state[sectionId]);
            TabRenderer.renderTabsInParagraph(paragraph);
          }
        });
      }

      /**
       * Adds a tabstop to a paragraph if not already present at that position
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {Object} newTabstop - The tabstop to add
       * @returns {boolean} - True if tabstop was added
       */
      function addTabstopToParagraph(paragraph, newTabstop) {
        // Get existing tabstops
        const tabstops = TabstopService.parseTabstopsFromElement(paragraph);
        
        // Check if a tabstop already exists at this position (within tolerance)
        const exists = tabstops.some(function(ts) {
          return Math.abs(ts.position - newTabstop.position) <= TABSTOP_POSITION_TOLERANCE;
        });
        
        if (exists) {
          // Not an error - just informational
          if (window.CONSTS && window.CONSTS.DEBUG) {
            handleError('Tabstop already exists near position ' + newTabstop.position, 'addTabstopToParagraph', null);
          }
          return false;
        }
        
        // Add the new tabstop
        tabstops.push(newTabstop);
        
        // Set tabstops on paragraph (will be sorted automatically)
        return TabstopService.setTabstopsOnElement(paragraph, tabstops);
      }
      
      /**
       * Removes a tabstop from a paragraph by position
       * @param {HTMLElement} paragraph - The paragraph element
       * @param {number} position - The position of the tabstop to remove (in points)
       * @returns {boolean} - True if tabstop was removed
       */
      function removeTabstopFromParagraph(paragraph, position) {
        // Get existing tabstops
        const tabstops = TabstopService.parseTabstopsFromElement(paragraph);
        
        // Find and remove the tabstop at the given position (within tolerance)
        const originalLength = tabstops.length;
        const filtered = tabstops.filter(function(ts) {
          return Math.abs(ts.position - position) > TABSTOP_POSITION_TOLERANCE;
        });
        
        // Check if a tabstop was actually removed
        if (filtered.length === originalLength) {
          // Not an error - just informational
          if (window.CONSTS && window.CONSTS.DEBUG) {
            handleError('No tabstop found at position ' + position, 'removeTabstopFromParagraph', null);
          }
          return false;
        }
        
        // Set the filtered tabstops on paragraph
        return TabstopService.setTabstopsOnElement(paragraph, filtered);
      }
      
      /**
       * Gets all selected paragraphs from the current selection
       * @returns {Array} - Array of paragraph elements
       */
      function getSelectedParagraphs() {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
          return [];
        }
        
        const range = selection.getRangeAt(0);
        
        // If selection is collapsed (just a caret), return empty
        // The activeParagraph will be used instead
        if (range.collapsed) {
          return [];
        }
        
        // Find all paragraphs that overlap with the selection
        const paragraphs = [];
        const container = range.commonAncestorContainer;
        
        // Get the nearest element (in case container is a text node)
        const containerElement = container.nodeType === Node.ELEMENT_NODE 
          ? container 
          : container.parentElement;
        
        if (!containerElement) {
          return [];
        }
        
        // Find paragraphs within the container
        const potentialParagraphs = containerElement.querySelectorAll 
          ? containerElement.querySelectorAll(TabstopService.PARAGRAPH_SELECTOR)
          : [];
        
        // Also check if container itself is a paragraph
        const containerParagraph = TabstopService.findParagraphElement(containerElement);
        
        if (potentialParagraphs.length === 0 && containerParagraph) {
          return [containerParagraph];
        }
        
        // Check each potential paragraph to see if it's in the selection
        potentialParagraphs.forEach(function(paragraph) {
          if (selection.containsNode(paragraph, true)) {
            paragraphs.push(paragraph);
          }
        });
        
        return paragraphs;
      }

      /**
       * Gets the parent paragraph's indentation values for nested paragraph context
       * @param {HTMLElement} parentParagraph - The parent <p pid="..."> element
       * @returns {Object} - { marginLeftPoints, marginRightPoints }
       */
      function getParentIndentation(parentParagraph) {
        if (!parentParagraph) {
          return { marginLeftPoints: 0, marginRightPoints: 0 };
        }
        const parentStyle = window.getComputedStyle(parentParagraph);
        return {
          marginLeftPoints: TabRenderer.pixelsToPoints(parseFloat(parentStyle.marginLeft) || 0),
          marginRightPoints: TabRenderer.pixelsToPoints(parseFloat(parentStyle.marginRight) || 0)
        };
      }

      /**
       * Updates indent indicators for a paragraph
       * When vm.activeNestedParagraph is set, shows effective (accumulated) indentation:
       * parent indentation + nested paragraph indentation
       * @param {HTMLElement} paragraph - The paragraph element (parent when nested)
       */
      function updateIndentIndicators(paragraph) {
        if (!paragraph) {
          vm.hasFirstLineIndent = false;
          vm.hasHangingIndent = false;
          vm.hasRightIndent = false;
          // When no paragraph, right indent should be at right margin
          vm.rightIndentPosition = vm.rightMarginPosition;
          vm.rightIndentPoints = 0;
          vm.parentBaseMarginLeft = 0;
          vm.parentBaseMarginRight = 0;
          return;
        }

        // Determine if we're in a nested paragraph context
        const nestedParagraph = vm.activeNestedParagraph;

        if (nestedParagraph) {
          // Nested paragraph: show effective (accumulated) indentation
          // Parent provides a base offset; nested paragraph's own indentation is additive
          const parentIndent = getParentIndentation(paragraph);
          vm.parentBaseMarginLeft = parentIndent.marginLeftPoints;
          vm.parentBaseMarginRight = parentIndent.marginRightPoints;

          // Get nested paragraph's own indentation
          const nestedStyle = window.getComputedStyle(nestedParagraph);
          const nestedTextIndent = parseFloat(nestedStyle.textIndent) || 0;
          const nestedMarginLeft = parseFloat(nestedStyle.marginLeft) || 0;
          const nestedMarginRight = parseFloat(nestedStyle.marginRight) || 0;

          const nestedTextIndentPoints = TabRenderer.pixelsToPoints(nestedTextIndent);
          const nestedMarginLeftPoints = TabRenderer.pixelsToPoints(nestedMarginLeft);
          const nestedMarginRightPoints = TabRenderer.pixelsToPoints(nestedMarginRight);

          // Effective values = parent base + nested own values
          // firstLineIndentPoints stores textIndent (relative to hanging) - same as nested's own textIndent
          vm.firstLineIndentPoints = nestedTextIndentPoints;
          // hangingIndentPoints stores effective marginLeft (parent + nested)
          vm.hangingIndentPoints = vm.parentBaseMarginLeft + nestedMarginLeftPoints;
          // rightIndentPoints stores effective marginRight (parent + nested)
          vm.rightIndentPoints = vm.parentBaseMarginRight + nestedMarginRightPoints;
        } else {
          // Regular (non-nested) paragraph
          vm.parentBaseMarginLeft = 0;
          vm.parentBaseMarginRight = 0;

          const paragraphStyle = window.getComputedStyle(paragraph);
          const textIndent = parseFloat(paragraphStyle.textIndent) || 0;
          const marginLeft = parseFloat(paragraphStyle.marginLeft) || 0;
          const marginRight = parseFloat(paragraphStyle.marginRight) || 0;

          vm.firstLineIndentPoints = TabRenderer.pixelsToPoints(textIndent);
          vm.hangingIndentPoints = TabRenderer.pixelsToPoints(marginLeft);
          vm.rightIndentPoints = TabRenderer.pixelsToPoints(marginRight);
        }

        vm.hasFirstLineIndent = vm.firstLineIndentPoints !== 0;
        vm.hasHangingIndent = vm.hangingIndentPoints > 0;
        vm.hasRightIndent = vm.rightIndentPoints > 0;

        // Always update positions, even when indent is 0, so indicators stay in sync
        // Convert points offset to points position: 0pt = vm.paddingLeftPoints, Xpt = vm.paddingLeftPoints + Xpt
        // This maintains the base offset (forbidden zone) correctly, all in points
        // First-line absolute position = hanging (marginLeft) + first-line relative (textIndent)
        const firstLineAbsolutePosition = vm.hangingIndentPoints + vm.firstLineIndentPoints;
        vm.firstLineIndentPosition = clampLeftDisplayPosition(pointsToRulerPoints(firstLineAbsolutePosition));
        vm.hangingIndentPosition = clampLeftDisplayPosition(pointsToRulerPoints(vm.hangingIndentPoints));

        // Right indent position: starts at right margin, moves left (increases 'right' value) when margin-right > 0
        // Position = rightMarginPosition + marginRight (in pixels)
        vm.rightIndentPosition = vm.rightMarginPosition + TabRenderer.pointsToPixels(vm.rightIndentPoints);
      }

      /**
       * Updates tabstop markers for a given paragraph (single-paragraph mode)
       * @param {HTMLElement} paragraph - The paragraph element
       */
      vm.updateTabstopsForParagraph = function(paragraph) {
        // Reset multi-paragraph selection state
        vm.isMultiParagraphSelection = false;
        vm.selectedParagraphs = [];
        vm.selectedParagraphCount = 0;
        vm.partialTabstops = [];

        vm.activeParagraph = paragraph;

        if (!paragraph) {
          vm.tabstops = [];
          vm.hasFirstLineIndent = false;
          vm.hasHangingIndent = false;
          vm.hasRightIndent = false;
          vm.activeNestedParagraph = null;
          vm.parentBaseMarginLeft = 0;
          vm.parentBaseMarginRight = 0;
          return;
        }

        // Get tabstops from paragraph
        vm.tabstops = TabstopService.parseTabstopsFromElement(paragraph);

        // Update indent indicators (pass nested paragraph context if available)
        updateIndentIndicators(paragraph);
      };
      
      // Debounce timer for selection changes to avoid excessive layout churn
      let selectionChangeTimeout = null;
      const SELECTION_DEBOUNCE_MS = 60;

      /**
       * Handles selection changes to update ruler for active paragraph
       */
      function handleSelectionChange() {
        if (selectionChangeTimeout) {
          $timeout.cancel(selectionChangeTimeout);
        }

        selectionChangeTimeout = $timeout(function() {
          selectionChangeTimeout = null;

          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) {
            return;
          }

          const range = selection.getRangeAt(0);

          // Check if selection spans multiple paragraphs
          if (!range.collapsed) {
            const selectedParagraphs = getSelectedParagraphs();
            if (selectedParagraphs.length > 1) {
              // Multi-paragraph selection - update with merged view
              $scope.$apply(function() {
                updateForMultiParagraphSelection(selectedParagraphs);
              });
              return;
            }
          }

          // Single paragraph or collapsed selection
          const container = range.commonAncestorContainer;
          const paragraph = TabstopService.findParagraphElement(container);

          if (!paragraph) {
            return;
          }

          // Handle nested paragraphs: preserve nested context for indentation
          const blockData = paragraph.getBlockData();
          let para;
          let nestedPara = null;

          if (blockData && blockData.containingNode) {
            para = blockData.containingNode;
            // Check if the found paragraph is a nested <p> (has data-nested-paragraph-index)
            if (paragraph !== para && paragraph.hasAttribute('data-nested-paragraph-index')) {
              nestedPara = paragraph;
            }
          } else {
            para = paragraph;
          }

          // Only update if paragraph changed, nested paragraph changed, or we were in multi-select mode
          if (para !== vm.activeParagraph || nestedPara !== vm.activeNestedParagraph || vm.isMultiParagraphSelection) {
            $scope.$apply(function() {
              vm.activeNestedParagraph = nestedPara;
              vm.updateTabstopsForParagraph(para);
            });
          }
        }, SELECTION_DEBOUNCE_MS);
      }

      /**
       * Updates the ruler for a multi-paragraph selection
       * Shows common tabstops and partial tabstops
       * @param {Array} paragraphs - Array of selected paragraph elements
       */
      function updateForMultiParagraphSelection(paragraphs) {
        vm.selectedParagraphs = paragraphs;
        vm.isMultiParagraphSelection = true;
        vm.selectedParagraphCount = paragraphs.length;

        // Reset nested paragraph state for multi-selection
        vm.activeNestedParagraph = null;
        vm.parentBaseMarginLeft = 0;
        vm.parentBaseMarginRight = 0;

        // Use first paragraph as the "active" one for operations
        vm.activeParagraph = paragraphs[0];

        // Get all tabstops from all paragraphs
        const allTabstopsByParagraph = paragraphs.map(p => TabstopService.parseTabstopsFromElement(p));

        // Build a map of position -> {count, tabstop}
        const positionMap = new Map();
        allTabstopsByParagraph.forEach(tabstops => {
          tabstops.forEach(ts => {
            // Use rounded position as key (to handle floating point)
            const key = Math.round(ts.position);
            if (positionMap.has(key)) {
              const entry = positionMap.get(key);
              entry.count++;
              // Merge alignment/leader - if they differ, mark as mixed
              if (entry.tabstop.alignment !== ts.alignment) {
                entry.mixedAlignment = true;
              }
              if (entry.tabstop.leader !== ts.leader) {
                entry.mixedLeader = true;
              }
            } else {
              positionMap.set(key, {
                count: 1,
                tabstop: { ...ts },
                mixedAlignment: false,
                mixedLeader: false
              });
            }
          });
        });

        // Separate into common (all paragraphs) and partial (some paragraphs)
        vm.tabstops = [];
        vm.partialTabstops = [];

        positionMap.forEach((entry, position) => {
          const tabstop = {
            position: entry.tabstop.position,
            alignment: entry.mixedAlignment ? 'left' : entry.tabstop.alignment, // Default to left if mixed
            leader: entry.mixedLeader ? 'none' : entry.tabstop.leader, // Default to none if mixed
            paragraphCount: entry.count,
            isMixed: entry.mixedAlignment || entry.mixedLeader
          };

          if (entry.count === paragraphs.length) {
            // Common to all paragraphs
            vm.tabstops.push(tabstop);
          } else {
            // Partial - only in some paragraphs
            vm.partialTabstops.push(tabstop);
          }
        });

        // Sort by position
        vm.tabstops.sort((a, b) => a.position - b.position);
        vm.partialTabstops.sort((a, b) => a.position - b.position);

        // Update indent indicators from first paragraph
        updateIndentIndicators(vm.activeParagraph);
      }
      
      /**
       * Initializes the ruler
       */
      vm.init = function() {
        // Initial ruler update after DOM is ready
        // Use a longer delay to ensure document is fully laid out and positioned
        $timeout(function() {
          vm.updateRuler();
        }, INIT_DELAY_MS);
        
        // Also update on next animation frame to catch any late layout changes
        requestAnimationFrame(function() {
          $scope.$apply(function() {
            vm.updateRuler();
          });
        });
        
        // Listen for selection changes
        document.addEventListener('selectionchange', handleSelectionChange);
        
        // Listen for window resize (with debouncing to prevent excessive updates)
        vm.resizeHandler = function() {
          // Clear any pending resize timeout
          if (vm.resizeTimeout) {
            $timeout.cancel(vm.resizeTimeout);
          }
          
          // Only update if ruler is visible
          if (vm.isVisible) {
            // Debounce: wait for debounce delay after last resize event before updating
            vm.resizeTimeout = $timeout(function() {
              $scope.$apply(function() {
                vm.updateRuler();
              });
              vm.resizeTimeout = null;
            }, RESIZE_DEBOUNCE_MS);
          }
        };
        window.addEventListener('resize', vm.resizeHandler);
        
        // Listen for keyboard shortcut (Ctrl+Shift+R / Cmd+Shift+R)
        document.addEventListener('keydown', function(event) {
          // Skip if feature is disabled via feature flag
          if (vm.isFeatureDisabled) {
            return;
          }
          if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'R') {
            event.preventDefault();
            $scope.$apply(function() {
              vm.toggleVisibility();
            });
          }
        });
        
        // Listen for tabstop changes via $rootScope events
        $scope.$on('tabstops:changed', function(event, data) {
          if (data && data.paragraph) {
            vm.updateTabstopsForParagraph(data.paragraph);
          }
        });

        // Note: $rootScope.lpRuler API is exposed in the link function
        // after feature flag check passes
      };
      
      /**
       * Cleanup on destroy
       */
      $scope.$on('$destroy', function() {
        document.removeEventListener('selectionchange', handleSelectionChange);
        
        // Clean up resize handler
        if (vm.resizeHandler) {
          window.removeEventListener('resize', vm.resizeHandler);
        }
        
        // Cancel any pending resize timeout
        if (vm.resizeTimeout) {
          $timeout.cancel(vm.resizeTimeout);
          vm.resizeTimeout = null;
        }
        
        // Cancel any pending drag safeApply timeout
        if (vm.dragSafeApplyTimeout) {
          $timeout.cancel(vm.dragSafeApplyTimeout);
          vm.dragSafeApplyTimeout = null;
        }
        
        // Clean up context menu event listeners
        closeContextMenu();
        
        delete $rootScope.lpRuler;
      });
      
      // Initialize
      vm.init();
    }
    
    return {
      restrict: 'EA',
      scope: {},
      controller: ['$scope', '$element', '$sce', RulerController],
      controllerAs: 'vm',
      template: createRulerTemplate(),
      link: function(scope, element, attrs) {
        // Expose the ruler API on $rootScope for external control
        $rootScope.lpRuler = {
          show: function() { scope.vm.show(); },
          hide: function() { scope.vm.hide(); },
          toggle: function() { scope.vm.toggleVisibility(); },
          update: function() { scope.vm.updateRuler(); },
          updateTabstopsForParagraph: function(paragraph) { 
            if (paragraph) {
              scope.$apply(function() {
                scope.vm.updateTabstopsForParagraph(paragraph);
              });
            }
          },
          isVisible: function() { return scope.vm.isVisible; }
        };
        
        // Add contextmenu event listener using event delegation
        // Attach to the ruler element so it works even when tabstops are recreated
        var contextMenuHandler = function(event) {
          // Find the closest tabstop element
          const tabstopElement = event.target.closest('.lp-ruler__tabstop');
          if (tabstopElement && !tabstopElement.classList.contains('lp-ruler__tabstop--partial')) {
            event.preventDefault();
            event.stopPropagation();
            
            // Get the tabstop index and position from data attributes
            const indexAttr = tabstopElement.getAttribute('data-tabstop-index');
            const positionAttr = tabstopElement.getAttribute('data-tabstop-position');
            
            // Get the tabstop from the scope
            scope.$apply(function() {
              let tabstop = null;
              let index = -1;
              
              // Try to use index first
              if (indexAttr !== null && scope.vm.tabstops) {
                index = parseInt(indexAttr, 10);
                if (scope.vm.tabstops[index]) {
                  tabstop = scope.vm.tabstops[index];
                }
              }
              
              // Fallback: find by position if index didn't work
              if (!tabstop && positionAttr !== null && scope.vm.tabstops) {
                const position = parseFloat(positionAttr);
                scope.vm.tabstops.forEach(function(ts, idx) {
                  if (Math.abs(ts.position - position) < 1) {
                    tabstop = ts;
                    index = idx;
                  }
                });
              }
              
              if (tabstop && index >= 0) {
                scope.vm.handleTabstopContextMenu(event, tabstop, index);
              }
            });
          }
        };
        
        // Wait for template to render, then attach listener
        $timeout(function() {
          const rulerElement = element[0].querySelector('.lp-ruler__content') || element[0];
          if (rulerElement) {
            rulerElement.addEventListener('contextmenu', contextMenuHandler);
            
            // Clean up on destroy
            scope.$on('$destroy', function() {
              rulerElement.removeEventListener('contextmenu', contextMenuHandler);
            });
          }
        }, 0);
      }
    };
  }
]);
